(function () {
  'use strict';

  /* The PDP is rendered by 23 independent sections - hero, trust bar,
     problem/solution, stats, studies, and so on down to the fixed furniture -
     and Shopify wraps each one in its own <section id="shopify-section-...">,
     so there is one .rgpdp element per section rather than one for the whole
     page.

     root stays the FIRST of them, because in template order that is the hero
     and initSticky's scroll-to-top fallback needs an element. Lookups, however,
     run against the document: the buy box lives in the hero while the sticky
     bar, cart drawer and lightbox are each their own section now, so a
     root-scoped $ would silently return null for all of them and take
     add-to-cart down with it.

     Widening the scope is safe because every selector in this file is either
     rgpdp- prefixed or a data-* attribute that exists nowhere else on the page -
     the header, footer, announcement bar and theme markup share none of them. */
  var root = document.querySelector('.rgpdp');
  if (!root) return;
  var scope = document;

  /* MOTION IS FORCED ON, site-wide. The visitor's reduced-motion setting is
     ignored on purpose - several of these sections only work animated - so
     this stands in for the media query and never matches. Every
     reduceMotion.matches below reads false, the live 'change' listener is
     skipped (no addEventListener), and the proof carousel's
     honour_reduced_motion setting has nothing left to honour. */
  var reduceMotion = { matches: false };

  /* ── Helpers ───────────────────────────────────────────────────────────── */

  function $(sel, ctx) { return (ctx || scope).querySelector(sel); }
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || scope).querySelectorAll(sel));
  }

  function on(el, evt, fn, opts) {
    if (el) el.addEventListener(evt, fn, opts || false);
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function scrollToEl(el) {
    if (!el) return;
    var behavior = reduceMotion.matches ? 'auto' : 'smooth';
    try {
      el.scrollIntoView({ behavior: behavior, block: 'start' });
    } catch (e) {
      el.scrollIntoView();
    }
  }

  /* ── Which element actually scrolls ────────────────────────────────────── */
  var DESKTOP = window.matchMedia
    ? window.matchMedia('(min-width: 990px)')
    : { matches: false };

  function pageWrapper() { return document.querySelector('.page-wrapper'); }

  function isWindowScroller(el) {
    return !el || el === window || el === document ||
           el === document.scrollingElement || el === document.documentElement || el === document.body;
  }

  function activeScroller() {
    if (DESKTOP.matches) {
      var pw = pageWrapper();
      if (pw) return pw;
    }
    return document.scrollingElement || document.documentElement;
  }

  function getTop(el) {
    if (isWindowScroller(el)) return window.pageYOffset || document.documentElement.scrollTop || 0;
    return el.scrollTop || 0;
  }

  function maxTop(el) {
    if (isWindowScroller(el)) {
      return Math.max(0, (document.documentElement.scrollHeight || 0) - window.innerHeight);
    }
    return Math.max(0, el.scrollHeight - el.clientHeight);
  }

  function setTop(el, top) {
    top = Math.max(0, Math.min(top, maxTop(el)));
    try {
      if (isWindowScroller(el)) window.scrollTo({ top: top, left: 0, behavior: 'instant' });
      else el.scrollTo({ top: top, left: 0, behavior: 'instant' });
    } catch (e) {
      if (isWindowScroller(el)) window.scrollTo(0, top);
      else el.scrollTop = top;
    }
  }

  function isFromActiveScroller(e) {
    if (!e || !e.target) return true;
    var active = activeScroller();
    var t = e.target;
    if (t.nodeType === 1) return t === active;
    return isWindowScroller(active);
  }

  /* ── Scroll lock ───────────────────────────────────────────────────────── */
  /* Reference-counted rather than a plain boolean, because more than one
     overlay can hold the lock - the cart drawer and the image lightbox each
     take one. Whichever closes first must not hand scrolling back to a page
     that is still covered by the other one.

     AND IT REMEMBERS WHERE THE PAGE WAS. .rgpdp-cart-locked takes the overflow
     off html, body and .page-wrapper together, which collapses the scrollable
     range under the reader's feet; the browser clamps the offset to what is
     left of it, and putting the overflow back does NOT put the offset back.
     The symptom is a page that returns a screen or two higher than it was left,
     every time the lightbox or the cart drawer closes.

     Note the theme's own lock - html[scroll-lock] in base.css - sets html and
     .page-wrapper but deliberately not body, which is most of why this one
     moves the page and that one does not. Saving the offset is the fix that
     holds either way, and it costs one read.

     Read off the live scroller before the FIRST lock goes on, written back
     after the LAST one comes off - nesting saves once, which is what the
     counter is for. The write has to come after the class is removed: setTop
     clamps to maxTop, and while the lock is on that maximum is zero. */
  var scrollLocks = 0;
  var lockedTop = 0;
  var lockedEl = null;
  function lockScroll(locked) {
    var before = scrollLocks;
    scrollLocks = Math.max(0, scrollLocks + (locked ? 1 : -1));

    if (before === 0 && scrollLocks > 0) {
      lockedEl = activeScroller();
      lockedTop = getTop(lockedEl);
    }

    document.documentElement.classList.toggle('rgpdp-cart-locked', scrollLocks > 0);

    if (before > 0 && scrollLocks === 0 && lockedEl) {
      setTop(lockedEl, lockedTop);
      lockedEl = null;
    }
  }

  /* ── Pinning the page to one element across an overlay ─────────────────── */
  /* lockScroll() puts the OFFSET back, and an offset is only the right answer
     while the page above the reader is exactly the height it was when the
     overlay went up. Often it is not. Taking the overflow off html re-lays the
     whole document out at scrollbar width, and putting it back re-lays it again;
     a lazy image, a web font or a scroll-snap container that re-settles during
     either pass moves everything below it by a few pixels. Restore the same
     number to a page that is now a different shape and the reader lands a little
     above where they were - which is the symptom, and why it shows up further
     down the page, where more has had a chance to shift, and not at the top.

     So this pins the ELEMENT the reader was looking at instead: read where its
     top edge sits in the viewport before the overlay opens, and after it closes
     put it back on that line, whatever the offset has to become to do it. The
     offset restore above still runs first and does most of the work; this only
     ever corrects the remainder.

     Re-checked over the following fifth of a second rather than once, because
     the things that move the page are not all finished when close() returns -
     a decode or a re-snap can land a frame or two later. setTop clamps, so a
     pin that cannot be honoured at the very bottom of the page settles as close
     as the page allows instead of fighting it. */
  function pinTo(el) {
    if (!el || !el.getBoundingClientRect) return null;
    var want = el.getBoundingClientRect().top;
    var live = true;

    function fix() {
      if (!live || !document.contains(el)) return;
      var drift = el.getBoundingClientRect().top - want;
      if (Math.abs(drift) < 0.5) return;
      var sc = activeScroller();
      setTop(sc, getTop(sc) + drift);
    }

    return function release() {
      fix();
      requestAnimationFrame(fix);
      setTimeout(fix, 60);
      setTimeout(function () { fix(); live = false; }, 220);
    };
  }

  /* ── Analytics context ─────────────────────────────────────────────────── */
  /* The shared tracker (1-custom-track.js) loads immediately before this file.
     Resolved lazily and allowed to be missing: if that asset ever fails, the
     page must still sell, so every tracking call degrades to a silent no-op. */
  function RT() { return window.RegaliaTrack || null; }
  function clean(obj) { var t = RT(); return t ? t.compact(obj) : obj; }
  function join(a, b) { var t = RT(); return t ? t.merge(a, b) : a; }

  var lastCart = null;

  function num(v, fallback) {
    var n = parseInt(v, 10);
    return isNaN(n) ? (fallback || 0) : n;
  }

  function selectedOffer() {
    var o = $('[data-offer].is-selected');
    if (o) return o;
    var input = $('[data-offer-input]:checked');
    return (input && input.closest) ? input.closest('[data-offer]') : null;
  }

  /* Read off the DOM rather than out of initBuyBox's closure. initBuyBox writes
     the reconciled variant id and selling-plan ids straight back onto the
     elements, so the markup stays the single source of truth and the three
     consumers - Meta, PostHog, Klaviyo - stay decoupled from it.

     THE LADDER IS THREE VARIANTS. Every tile carries its own data-variant-id and
     is added at quantity 1, so the variant id comes off the SELECTED TILE and
     the block's own attribute is only the fallback for a tile that could not
     resolve one. `units` is how many bottles that variant ships (1 / 3 / 5), not
     a cart quantity - which is what makes unit_price a per-bottle figure.
     free_shipping is the tier's own answer, decided in Liquid against the
     converted threshold, so this and the drawer can never disagree. */
  function offerContext() {
    var block = $('[data-buyblock]');
    var o = selectedOffer();
    if (!block || !o) return {};
    var d = o.dataset || {};
    var units = num(d.units, 1);
    var nowC = num(d.priceCents, 0);
    var wasC = num(d.wasCents, 0);
    var sub = d.mode === 'sub';
    var title = $('.rgpdp-buybox__title');

    return clean({
      offer_type: sub ? 'subscription' : 'one_time',
      offer_name: d.name || '',
      offer_frequency: d.freq || '',
      plan_key: d.planKey || '',
      selling_plan_id: (sub && d.sellingPlan) ? String(d.sellingPlan) : '',
      units: units,
      variant_id: d.variantId || block.getAttribute('data-variant-id') || '',
      product_handle: block.getAttribute('data-product-handle') || '',
      product_title: title ? title.textContent.replace(/\s+/g, ' ').trim() : '',
      price: nowC / 100,
      price_cents: nowC,
      compare_at: wasC / 100,
      compare_at_cents: wasC,
      unit_price: units ? Math.round(nowC / units) / 100 : 0,
      discount_pct: num(d.discount, 0),
      discount_code: d.code || '',
      subscription_code: sub ? (block.getAttribute('data-sub-code') || '') : '',
      free_shipping: d.freeShip === '1',
      currency: block.getAttribute('data-currency') || '',
      region: block.getAttribute('data-country') || '',
      /* The raw Shopify cart is UNDISCOUNTED until the automatic discounts land
         at checkout, so it is reported separately from our displayed price
         rather than folded into it. A mismatch between price_cents and
         shopify_cart_total_cents is expected, not a bug. */
      cart_item_count: lastCart ? (lastCart.item_count || 0) : 0,
      shopify_cart_total_cents: lastCart ? (lastCart.total_price || 0) : 0
    });
  }

  /* contents describes the CART LINE, not the pack: one unit of the offer's own
     variant, priced at the offer's price. It used to say quantity: units / item_price:
     unit_price, which was right while the ladder added the same variant 1x / 2x
     / 3x. It is not right now - a 3-bottle offer is one line of one variant - and
     a payload that disagrees with Shopify's own pixel about the shape of the
     cart is worse than a coarse one. The bottles-per-pack figure is still
     reported to PostHog as `units`. */
  function metaParams(ctx) {
    var id = ctx.variant_id ? String(ctx.variant_id) : '';
    var p = {
      content_type: 'product',
      content_name: ctx.product_title || '',
      value: ctx.price,
      currency: ctx.currency,
      num_items: 1
    };
    if (id) {
      p.content_ids = [id];
      p.contents = [{ id: id, quantity: 1, item_price: ctx.price }];
    }
    return p;
  }

  /* Fires the Meta standard event and the matching PostHog event, tied together
     by the pixel's eventID so the same action can be reconciled across both.

     metaEvent may be null, meaning "Shopify's own pixel already sends this
     standard event, leave it to them" - see the AddToCart and checkout call
     sites. The PostHog event still fires; it just carries no meta_event_id,
     because there is no Meta call of ours for it to be reconciled against.

     opts is handed straight to posthog.capture, so a caller that is about to
     navigate can ask for { transport: 'sendBeacon' } - see the checkout
     handler. */
  function trackOffer(metaEvent, phEvent, extra, opts) {
    var t = RT();
    var ctx = offerContext();
    if (!t) return ctx;
    var eventId = metaEvent ? t.fb(metaEvent, metaParams(ctx)) : '';
    t.capture(phEvent, join(join(ctx, { meta_event_id: eventId }), extra), opts);
    return ctx;
  }

  (function initTrackingPage() {
    var t = RT();
    if (t) { t.setPage('pdp'); return; }
    on(window, 'load', function () { var late = RT(); if (late) late.setPage('pdp'); });
  })();

  /* ── Gallery ───────────────────────────────────────────────────────────────
     ENDLESS IN BOTH DIRECTIONS. A copy of the whole run is parked either side
     of the originals, so five photographs lay out as

         1 2 3 4 5 | 1 2 3 4 5 | 1 2 3 4 5
         \__lead__/ \__real___/ \__tail___/

     and there is always another photo to reach, whichever way the reader is
     going. Once the track comes to REST on a copy it is jumped by exactly one
     run, to the identical original. The two positions are the same picture at
     the same size and nothing is animating, so the move cannot be seen; the
     reader carries on and the run they are standing in is never the one they
     think it is.

     AT REST, AND NOT ON SIGHT. A smooth scroll from an arrow tap fires the
     scroll handler on its way past, and rewriting scrollLeft mid-flight
     cancels the animation - what the reader would see is precisely the jump
     this arrangement exists to hide. So the handler is debounced (90ms with no
     scroll event means the track has stopped) and the position is checked
     against the cell's own left edge before anything moves, because a
     mandatory-snap track at rest is by definition aligned to one.

     THE COPIES DO NOT REACH THE VIEWER. Every cell carries
     .rgpdp-gallery__slide, which is what initLightbox() collects, so a plain
     clone would have the counter reading "1 / 15" over five photographs. Each
     copy is marked [data-lb-clone="n"] instead: the viewer drops those when it
     collects the group and reads n to know which original a tap on a copy
     means - the same arrangement the proof strip uses. This function runs
     first, so the copies are on the page before initLightbox() looks, and the
     gallery is never re-rendered afterwards.

     THE ARROWS NO LONGER HIDE. They used to disappear at the two ends; there
     are no ends left to mark. */
  (function initGallery() {
    var gallery = $('[data-gallery]');
    if (!gallery) return;

    var track = $('[data-gallery-track]', gallery);
    if (!track) return;

    var photos = $$('.rgpdp-gallery__slide', track);
    var thumbs = $$('[data-gallery-thumb]', gallery);
    var dots = $$('[data-gallery-dot]', gallery);
    var prev = $('[data-gallery-prev]', gallery);
    var next = $('[data-gallery-next]', gallery);
    var count = photos.length;  /* photographs */
    var cells = photos;         /* photographs AND their copies, in track order */
    var base = 0;               /* where the real run starts inside cells */
    var pos = 0;                /* index into cells - where the track is */
    var current = 0;            /* index into photos - what the thumbs mark */

    /* One photograph loops into itself and back, which is a still image with
       two arrows over it. Nothing below is built in that case. */
    if (count < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
      return;
    }

    var strip = $('.rgpdp-gallery__thumbs', gallery);

    function copyOf(photo, i) {
      var c = photo.cloneNode(true);
      /* Announced once, by the originals. */
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('data-lb-clone', String(i));
      /* aria-hidden over something still in the tab order is the one reliable
         way to land a screen reader on a control it then cannot describe, and
         there is a zoom button inside every one of these. */
      $$('button, [tabindex]', c).forEach(function (el) { el.tabIndex = -1; });
      var img = c.querySelector('img');
      if (img) {
        /* The copies of photo one would otherwise inherit the LCP hints the
           real one carries, on the same URL - three requests racing each other
           to the front of the queue for one file. */
        img.setAttribute('loading', 'lazy');
        img.removeAttribute('fetchpriority');
      }
      return c;
    }

    function buildLoop() {
      var lead = document.createDocumentFragment();
      var tail = document.createDocumentFragment();
      for (var i = 0; i < count; i++) {
        lead.appendChild(copyOf(photos[i], i));
        tail.appendChild(copyOf(photos[i], i));
      }
      track.appendChild(tail);
      track.insertBefore(lead, photos[0]);
      cells = $$('.rgpdp-gallery__slide', track);
      base = count;
    }

    /* Against the track's own left edge. offsetLeft is measured from the
       offsetParent - .rgpdp-gallery, the positioned one - and not from the
       scroller, so the track's own offset comes back off it. */
    function leftOf(i) {
      var cell = cells[i];
      return cell ? cell.offsetLeft - track.offsetLeft : 0;
    }

    function scrollTrack(x, smooth) {
      var behavior = (smooth && !reduceMotion.matches) ? 'smooth' : 'auto';
      try {
        track.scrollTo({ left: x, behavior: behavior });
      } catch (e) {
        track.scrollLeft = x;
      }
    }

    function revealThumb(index) {
      var t = thumbs[index];
      if (!strip || !t) return;
      var max = strip.scrollWidth - strip.clientWidth;
      if (max <= 0) return;
      var peek = t.offsetWidth * 0.6;
      var left = t.offsetLeft - strip.offsetLeft;
      var right = left + t.offsetWidth;
      var target;
      if (left - peek < strip.scrollLeft) target = left - peek;
      else if (right + peek > strip.scrollLeft + strip.clientWidth) target = right + peek - strip.clientWidth;
      else return;
      target = Math.max(0, Math.min(target, max));
      try {
        strip.scrollTo({ left: target, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      } catch (e) {
        strip.scrollLeft = target;
      }
    }

    /* Takes a CELL index and reduces it to the photograph it is a picture of,
       so the strip marks the same thumb whichever of the three runs is on
       screen. The modulo is written the long way because a negative index
       would come back negative from a bare one. */
    function markCurrent(index) {
      current = ((index % count) + count) % count;
      thumbs.forEach(function (t, i) {
        t.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
      dots.forEach(function (d, i) {
        d.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
      revealThumb(current);
    }

    /* Puts the track on the REAL run's copy of whatever is already showing,
       instantly. Same picture, same place, so there is nothing to see - and
       this is the one move that turns three finite runs into a run without an
       end. Call it only when the track is at rest. */
    function recentre() {
      var real = base + current;
      if (real === pos) return;
      pos = real;
      scrollTrack(leftOf(pos), false);
    }

    function goTo(index, smooth) {
      pos = Math.max(0, Math.min(index, cells.length - 1));
      scrollTrack(leftOf(pos), smooth);
      markCurrent(pos);
    }

    /* One photo along, either way. Stepping off the end of the real run lands
       in a copy and re-centring cleans that up once the scroll settles - but a
       reader holding down an arrow can walk a whole run of copies before it
       settles at all, so a step that would fall off the track re-centres first
       and takes its step from there. That is why the runs either side are a
       full set each rather than one cell: it is also how far a hard fling can
       carry before the snap catches it. */
    function step(delta) {
      if (pos + delta < 0 || pos + delta > cells.length - 1) recentre();
      goTo(pos + delta, true);
    }

    var syncFromScroll = debounce(function () {
      var mid = track.scrollLeft + track.clientWidth / 2;
      var best = 0, bestDist = Infinity;
      cells.forEach(function (s, i) {
        var centre = s.offsetLeft - track.offsetLeft + s.offsetWidth / 2;
        var dist = Math.abs(centre - mid);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      pos = best;
      markCurrent(pos);
      /* Two pixels of slack: offsetLeft comes back as a rounded integer and
         the snap position it is being compared against does not. */
      if (Math.abs(track.scrollLeft - leftOf(pos)) <= 2) recentre();
    }, 90);

    on(track, 'scroll', syncFromScroll, { passive: true });

    thumbs.forEach(function (t, i) { on(t, 'click', function () { goTo(base + i, true); }); });
    dots.forEach(function (d, i) { on(d, 'click', function () { goTo(base + i, true); }); });
    on(prev, 'click', function () { step(-1); });
    on(next, 'click', function () { step(1); });

    on(gallery, 'keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    });

    buildLoop();
    markCurrent(0);

    /* Park the track on the real run. Until this lands the reader is looking
       at the LEAD run's copy of photo one - the same picture, in the same
       place - so there is nothing to see when it does, and nothing broken if
       it never gets the chance.

       The script is deferred and the stylesheet is in the head, so the track
       has a width by now; the guard is for the case where it somehow does not,
       which would otherwise strand the gallery at the far left of the lead run
       with nothing behind it to swipe back into. */
    function home() {
      if (!(track.clientWidth > 0)) return false;
      recentre();
      return true;
    }
    if (!home()) on(window, 'load', home);

    on(window, 'resize', debounce(syncFromScroll, 200), { passive: true });
  })();

  /* ── Hero gallery: its height, for the sticky offset ───────────────────── */
  /* From 768 the gallery is position: sticky, and when it is taller than the
     screen the stylesheet pins it by its bottom edge instead of its top - see
     .rgpdp-gallery in the 768 block of 1-custom-pdp.css. CSS cannot read an
     element's own height, so this publishes it as --rgpdp-gallery-h and the
     stylesheet does the arithmetic.

     A ResizeObserver rather than a resize listener, because the height moves
     without the window changing size: the theme drawer squeezes .page-wrapper,
     and a late web font rewraps the desktop testimonial under the thumbs.

     Its own block, not part of initGallery, because that returns early for a
     one-photo product and a one-photo gallery still sticks. With no
     ResizeObserver nothing is published and the gallery pins by its top. */
  (function initGalleryStick() {
    var gallery = $('[data-gallery]');
    if (!gallery || !window.ResizeObserver) return;
    new ResizeObserver(function () {
      gallery.style.setProperty('--rgpdp-gallery-h', gallery.offsetHeight + 'px');
    }).observe(gallery);
  })();

  /* ── Q&A carousel ──────────────────────────────────────────────────────────
     1-custom-pdp-replica-qa. Only the 16:9 visuals are in the scrolling track;
     the title and body above it are swapped to match as the track moves.

     Written as a loop over [data-qa] rather than a single lookup because,
     unlike the gallery, nothing about this shape says there can only be one of
     them on a page. */
  (function initQaCarousels() {
    $$('[data-qa]').forEach(function (qa) {
      var track = $('[data-qa-track]', qa);
      if (!track) return;

      var slides = $$('.rgqa__slide', track);
      var panels = $$('[data-qa-panel]', qa);
      var dots = $$('[data-qa-dot]', qa);
      var prev = $('[data-qa-prev]', qa);
      var next = $('[data-qa-next]', qa);
      var current = 0;
      var inviting = false;

      if (slides.length < 2) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
        return;
      }

      /* The right arrow pulses to say "there is more here, tap me", and stops
         the first time the reader moves the carousel by ANY means - arrow, dot,
         swipe or key - because at that point they have understood it and the
         animation is just noise. Deliberately held in this variable and written
         nowhere else, so every page load starts the invitation again.

         Added from here rather than in the markup so that a page where this
         script never runs does not pulse an arrow that cannot do anything. */
      if (next) { next.classList.add('is-inviting'); inviting = true; }
      function settle() {
        if (!inviting) return;
        inviting = false;
        if (next) next.classList.remove('is-inviting');
      }

      function markCurrent(index) {
        current = Math.max(0, Math.min(index, slides.length - 1));
        panels.forEach(function (p, i) {
          p.classList.toggle('is-current', i === current);
          p.setAttribute('aria-hidden', i === current ? 'false' : 'true');
        });
        dots.forEach(function (d, i) {
          d.setAttribute('aria-current', i === current ? 'true' : 'false');
        });
        if (prev) prev.hidden = current === 0;
        if (next) next.hidden = current === slides.length - 1;
      }

      function goTo(index, smooth) {
        var slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
        if (!slide) return;
        var behavior = (smooth && !reduceMotion.matches) ? 'smooth' : 'auto';
        try {
          track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: behavior });
        } catch (e) {
          track.scrollLeft = slide.offsetLeft - track.offsetLeft;
        }
        markCurrent(index);
      }

      var syncFromScroll = debounce(function () {
        var mid = track.scrollLeft + track.clientWidth / 2;
        var best = 0, bestDist = Infinity;
        slides.forEach(function (s, i) {
          var centre = s.offsetLeft - track.offsetLeft + s.offsetWidth / 2;
          var dist = Math.abs(centre - mid);
          if (dist < bestDist) { bestDist = dist; best = i; }
        });
        if (best !== current) settle();
        markCurrent(best);
      }, 90);

      on(track, 'scroll', syncFromScroll, { passive: true });

      dots.forEach(function (d, i) { on(d, 'click', function () { settle(); goTo(i, true); }); });
      on(prev, 'click', function () { settle(); goTo(current - 1, true); });
      on(next, 'click', function () { settle(); goTo(current + 1, true); });

      on(track, 'keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); settle(); goTo(current - 1, true); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); settle(); goTo(current + 1, true); }
      });

      markCurrent(0);
      on(window, 'resize', debounce(syncFromScroll, 200), { passive: true });
    });
  })();

  /* ── Testimonial carousel ──────────────────────────────────────────────────
     1-custom-pdp-testimonials. The scrolling itself is the platform's - the
     track is an overflow-x container with scroll-snap, same as the gallery - so
     everything here is arrows and bookkeeping. Kill this function and the
     carousel still swipes; it just keeps two arrows that do nothing, which is
     also what the hero gallery does.

     Written as a loop rather than a single lookup for the reason the Q&A
     carousel is: nothing about this shape says there can only be one per page.

     Full screen is NOT handled here. The track carries [data-lightbox-group]
     and each photo .rgpdp-gallery__slide, so initLightbox() below picks them up
     with everything else. */
  (function initReviewCarousels() {
    $$('[data-rgrev]').forEach(function (rev) {
      var track = $('[data-rgrev-track]', rev);
      if (!track) return;

      var cards = $$('.rgrev__card', track);
      var prev = $('[data-rgrev-prev]', rev);
      var next = $('[data-rgrev-next]', rev);
      var current = 0;

      if (cards.length < 2) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
        return;
      }

      /* Which card is on the centre line at a given scroll position. Read from
         geometry rather than counted, because one press does not always move
         the run by one card: at both ends the target is out of reach and the
         track stays where it is. */
      function nearest(at) {
        var mid = at + track.clientWidth / 2;
        var best = 0, bestDist = Infinity;
        cards.forEach(function (c, i) {
          var centre = c.offsetLeft - track.offsetLeft + c.offsetWidth / 2;
          var dist = Math.abs(centre - mid);
          if (dist < bestDist) { bestDist = dist; best = i; }
        });
        return best;
      }

      /* THE ARROWS GO ON THE SCROLL RANGE, NOT ON THE INDEX. From 768px there
         are three cards in the stage, and a centre-snapping run that wide comes
         to rest with card TWO on the centre line - so `current === 0` is a
         state the track never reaches at that width, and testing for it left a
         live prev arrow at the left end that scrolled nowhere when it was
         pressed, and a next arrow whose first press was eaten getting `current`
         back in step. The ends of the range mean the same thing at both widths.
         The 1px of slack is for a fractional scrollWidth. */
      function settle(at) {
        var max = track.scrollWidth - track.clientWidth;
        current = nearest(at);
        if (prev) prev.hidden = at <= 1;
        if (next) next.hidden = at >= max - 1;
      }

      /* The gallery and the Q&A carousel both scroll a slide's left edge to the
         track's left edge, because their slides are the full width of the
         track. These are narrower than the track by twice the peek, and snap on
         their CENTRE, so the target has to be offset by the leftover width or
         every arrow press would land half a peek off and the snap would drag it
         back.

         The clamp is ours rather than scrollTo's, because `current` has to come
         from where the track will actually stop: at either end the card that
         was asked for cannot be centred, and taking the index on trust left the
         next press asking for a scroll that had already happened. */
      function goTo(index, smooth) {
        var card = cards[Math.max(0, Math.min(index, cards.length - 1))];
        if (!card) return;
        var max = Math.max(0, track.scrollWidth - track.clientWidth);
        var left = card.offsetLeft - track.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
        left = Math.max(0, Math.min(left, max));
        var behavior = (smooth && !reduceMotion.matches) ? 'smooth' : 'auto';
        try {
          track.scrollTo({ left: left, behavior: behavior });
        } catch (e) {
          track.scrollLeft = left;
        }
        settle(left);
      }

      function syncFromScroll() { settle(track.scrollLeft); }

      on(track, 'scroll', debounce(syncFromScroll, 90), { passive: true });

      on(prev, 'click', function () { goTo(current - 1, true); });
      on(next, 'click', function () { goTo(current + 1, true); });

      on(track, 'keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1, true); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1, true); }
      });

      /* Read, not assumed to be zero: three-up starts on card two, and a track
         restored by a back-navigation starts wherever it was left. */
      syncFromScroll();
      on(window, 'resize', debounce(syncFromScroll, 200), { passive: true });
    });
  })();


  /* ── Ingredient carousel ───────────────────────────────────────────────────
     1-custom-pdp-ingredients. The same shape as the testimonial carousel above
     - the track is an overflow-x container with scroll-snap and the platform
     does the scrolling - so all this does is keep the progress bar in step, let
     a segment be pressed to jump, and step the rail from the two desktop
     arrows. Kill it and the rail still swipes; the bar just stops moving and
     the arrows sit there doing nothing, which is how the hero gallery behaves.

     THE CARDS SNAP TO THEIR LEFT EDGE, not their centre, because two or three
     of them are in the stage at once above 40em and a centred snap would leave
     the row hanging off both sides. So "which card are we on" is the card whose
     left edge is nearest the track's, not the one nearest the middle.

     OFFSETS ARE MEASURED AGAINST CARD ONE rather than against the track,
     because the track carries a gutter's worth of padding-inline (it is
     full-bleed, with the first card pulled back in line with the heading). Card
     one at rest IS scrollLeft 0, so the distance from it is the scroll position
     that puts any other card in the same place, at any width, without this
     having to read the padding back out of the computed style.

     THE BAR IS FILLED BY A FRACTION, NOT SWITCHED ON. Every segment carries a
     --rging-fill between 0 and 1 and paints itself at that strength (see
     .rging__seg::after), so halfway between two cards both of their segments
     are half filled and the colour crosses from one to the next WITH the swipe
     instead of waiting for the scroll to settle and snapping. aria-current is
     still one segment at a time, and still debounced - a fraction means nothing
     to a screen reader, and announcing every card a long scroll passes through
     would be worse than announcing the one it lands on.

     THE TAIL OF THE STOP LIST IS SPREAD, NOT CLAMPED. The last cards can never
     bring their left edge to the track's - there is not enough run left behind
     them - so their scroll positions all collapse onto the end of the range,
     and a fill mapped straight off the card offsets would stall two or three
     segments short with no scroll left to move it. measure() divides whatever
     run is left between the segments that have to share it, so the fill arrives
     at the last one exactly as the scroll does. */
  (function initIngredientCarousels() {
    $$('[data-rging]').forEach(function (ing) {
      var track = $('[data-rging-track]', ing);
      if (!track) return;

      var cards = $$('.rging__card', track);
      var segs = $$('[data-rging-seg]', ing);
      var prev = $('[data-rging-prev]', ing);
      var next = $('[data-rging-next]', ing);
      if (!cards.length) return;

      var last = cards.length - 1;
      var current = -1;
      var stops = [];
      var fills = segs.map(function () { return -1; });

      function markCurrent(index) {
        index = Math.max(0, Math.min(index, last));
        if (index === current) return;
        current = index;
        segs.forEach(function (s, i) {
          s.setAttribute('aria-current', i === current ? 'true' : 'false');
        });
      }

      function offsetOf(index) {
        return cards[index].offsetLeft - cards[0].offsetLeft;
      }

      /* THE ARROWS GO ON THE SCROLL RANGE, NOT ON THE INDEX, the same as the
         testimonial rail's. The left end is honest either way - a left-snapping
         card one IS scrollLeft 0 - but the right end is not: the last two or
         three cards can never bring their left edge to the track's, so their
         stops are the spread ones measure() invents and `current === last` is
         reached before the scroll runs out. Testing the range means the next
         arrow disappears exactly when there is nothing left to scroll to.

         Not called from goTo(): a smooth scroll has not moved yet when goTo
         returns, and the scroll handler below runs it all the way to the stop.
         The 1px of slack is for a fractional scrollWidth.

         A single card needs no special case. scrollWidth === clientWidth there,
         so max is 0 and both tests hide their arrow. */
      function paintArrows() {
        var max = track.scrollWidth - track.clientWidth;
        if (prev) prev.hidden = track.scrollLeft <= 1;
        if (next) next.hidden = track.scrollLeft >= max - 1;
      }

      /* One scroll position per card, measured in a single pass and read on
         every frame after it: positionOf() runs off a scroll event and cannot
         be reading offsetLeft back out of the layout while it does. */
      function measure() {
        var max = Math.max(0, track.scrollWidth - track.clientWidth);
        var raw = cards.map(function (c, i) { return offsetOf(i); });
        var reach = 0;
        while (reach <= last && raw[reach] < max) reach++;
        stops = raw.slice(0, reach);
        if (reach <= last) {
          var from = reach > 0 ? raw[reach - 1] : 0;
          var share = (max - from) / (last - reach + 1);
          for (var i = reach; i <= last; i++) stops.push(from + share * (i - reach + 1));
        }
      }

      /* Where the scroll is, as a fractional card index - 2.5 is exactly
         between the third card and the fourth. */
      function positionOf() {
        var x = track.scrollLeft;
        for (var i = 0; i < last; i++) {
          if (x <= stops[i]) return i;
          if (x < stops[i + 1]) return i + (x - stops[i]) / (stops[i + 1] - stops[i]);
        }
        return last;
      }

      /* Full at the segment's own stop, empty a whole card away, straight line
         between the two. rAF-gated, so a burst of scroll events costs one paint
         rather than one each, and unchanged fractions are not written back at
         all - a swipe would otherwise touch nine inline styles a frame. */
      var painting = false;
      function paint() {
        if (painting || !segs.length) return;
        painting = true;
        requestAnimationFrame(function () {
          painting = false;
          var pos = positionOf();
          segs.forEach(function (s, i) {
            var fill = 1 - Math.abs(pos - i);
            fill = fill < 0 ? 0 : (fill > 1 ? 1 : fill);
            if (Math.abs(fill - fills[i]) < 0.004) return;
            fills[i] = fill;
            s.style.setProperty('--rging-fill', fill.toFixed(3));
          });
        });
      }

      function goTo(index, smooth) {
        index = Math.max(0, Math.min(index, last));
        var behavior = (smooth && !reduceMotion.matches) ? 'smooth' : 'auto';
        try {
          track.scrollTo({ left: offsetOf(index), behavior: behavior });
        } catch (e) {
          track.scrollLeft = offsetOf(index);
        }
        markCurrent(index);
        paint();
      }

      var syncFromScroll = debounce(function () {
        markCurrent(Math.round(positionOf()));
      }, 90);

      on(track, 'scroll', function () { paint(); paintArrows(); syncFromScroll(); }, { passive: true });

      segs.forEach(function (s, i) { on(s, 'click', function () { goTo(i, true); }); });

      /* Same step as the arrow keys below, deliberately: one card a press, and
         goTo() clamps at both ends. */
      on(prev, 'click', function () { goTo(current - 1, true); });
      on(next, 'click', function () { goTo(current + 1, true); });

      on(track, 'keydown', function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1, true); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1, true); }
      });

      measure();
      markCurrent(0);
      paint();
      /* Reads the scroll position rather than assuming the start, so the left
         arrow is already gone on the first paint instead of flashing in and
         out on the first scroll event. */
      paintArrows();
      on(window, 'resize', debounce(function () {
        measure();
        paint();
        paintArrows();
        syncFromScroll();
      }, 200), { passive: true });
    });
  })();


  /* ── Proof carousel ────────────────────────────────────────────────────────
     1-custom-pdp-proof. Not a carousel in the sense the two above are: there
     are no dots, no arrows and no stops to sit on. The strip runs, forever, one
     way, and a reader can take it over at any point and hand it straight back.

     THE DRIFT IS ONE WEB ANIMATIONS CALL - see retime() - and nothing in here
     runs it frame by frame. This function measures the strip, hands the
     animation a distance and a duration, and pauses or plays it. There is no
     rAF loop left at all.

     That is a rewrite of what was here before, and the reason is that the old
     one was visibly rough. It moved the strip by writing scrollLeft a frame at
     a time, and at 30px/s that is half a pixel a frame - which engines round to
     whole pixels, so the run advanced 0, 0, 1, 1, 2 and read as a stutter
     rather than as motion. Reading the offset back each frame to notice the
     reader's own scrolling forced a layout on top of it. A transform has
     neither problem: it is sub-pixel, and as a declarative animation it runs on
     the compositor, so the drift is smooth even while the main thread is busy.

     THE READER AND THE DRIFT MOVE DIFFERENT THINGS, which is what makes the
     rest of this small. A swipe moves the TRACK's scroll position; the
     animation moves the STRIP inside it. They compose without either knowing
     about the other, so there is no adopting the reader's position, no settling
     timer, and no way for the two to fight. Press and hold pauses the animation
     where it stands - animation-play-state holds the phase - so letting go
     carries on from the same pixel.

     A REAL LOOP. The cards are cloned until the row is several sets long, and
     the animation translates by exactly one set per cycle, which lands on
     identical pixels; the reader's own scroll offset is wrapped by whole sets
     too, in the scroll handler, once it drifts a full set out of its window.
     Neither seam is visible and there is no end to reach.

     THE CLONES DO NOT REACH THE VIEWER. Every photo carries
     .rgpdp-gallery__slide, so a naive clone would have initLightbox() counting
     "1 / 24" over four photographs. Each cloned frame is marked
     [data-lb-clone="n"] instead: the viewer skips those when it collects the
     group, and reads n to know which of the four originals a tap on a clone
     means. Nothing is unclickable and nothing is counted twice.

     "REDUCE MOTION" NEVER STOPS IT. data-rgproof-motion is still read, but
     reduceMotion at the top of this file never matches - motion is forced on
     site-wide - so honour_reduced_motion has no effect either way.

     Killing this function leaves a strip of four cards that swipes and
     arrow-keys exactly as it would have anyway - the clones and the animation
     are both built from here, so nothing is left half-done. */
  (function initProofCarousels() {
    $$('[data-rgproof]').forEach(function (proof) {
      var track = $('[data-rgproof-track]', proof);
      var strip = $('[data-rgproof-strip]', proof);
      if (!track || !strip) return;

      var cards = $$('.rgproof__card', strip);
      if (cards.length < 2) return;

      /* Pixels per second, off the schema. An explicit 0 is "leave it still",
         and it leaves the strip un-cloned as well - a still row of twenty-four
         cards is five repeats of a set nobody asked to repeat.

         A MISSING attribute falls back rather than bailing: this file and the
         section markup upload separately, and a page still serving the older
         section would otherwise kill the drift silently, with nothing on screen
         to say why. */
      var attr = proof.getAttribute('data-rgproof-speed');
      var speed = (attr === null || attr === '') ? 30 : parseFloat(attr);
      if (!(speed > 0)) return;

      var honourReduced = proof.getAttribute('data-rgproof-motion') !== 'always';

      var LEAD = 1;      /* sets of travel kept BEHIND the reader, to swipe back into */
      var NUDGE = 6;     /* px of mouse travel that makes a press a drag rather than a tap */

      var setW = 0;      /* one set of cards, plus the gap that follows it */
      var lo = 0, hi = 0;/* the window the reader's own offset is kept inside */
      var built = false;
      var seen = false;  /* the section is on screen */
      var down = false;  /* a finger or a mouse button is on the strip NOW */
      var dragging = false, dragFrom = 0, dragAt = 0, dragged = false;
      var wrapping = false;
      var tries = 0;
      var anim = null;   /* the drift. See retime(). */
      var animW = 0;     /* the set width the drift was built for */

      /* Tapping a photo opens a full-screen viewer over the top, and a strip
         that kept moving behind it would be somewhere else by the time the
         reader closed it.

         The VIEWER's own class, watched directly, rather than the shared
         .rgpdp-cart-locked that lockScroll() writes. That flag belongs to the
         cart drawer as much as to the lightbox, and it is reference-counted -
         one unbalanced lockScroll(true) anywhere on the page and this strip
         would freeze for good, with nothing to connect the two. */
      var viewer = $('[data-lightbox]');
      function covered() { return !!viewer && viewer.classList.contains('is-open'); }
      function silenced() { return honourReduced && reduceMotion.matches; }

      function gapPx() {
        var cs = window.getComputedStyle ? window.getComputedStyle(strip) : null;
        var g = cs ? parseFloat(cs.columnGap || cs.gap) : 0;
        return g > 0 ? g : 0;
      }

      /* First card's left edge to the last card's right edge, plus the gap that
         separates the set from the one after it - i.e. exactly how far the strip
         has to travel before it looks like itself again, which is both the
         animation's distance and the reader's wrap step. */
      function oneSet() {
        var first = cards[0], end = cards[cards.length - 1];
        return (end.offsetLeft + end.offsetWidth) - first.offsetLeft + gapPx();
      }

      /* HOW MANY COPIES. The reader's offset is wrapped back into [lo, lo+setW)
         once it strays a whole set out of it, so the furthest right they can
         reach before that happens is lo + 2 sets; the animation adds up to one
         more set on top, and the viewport itself has to be filled beyond that.
         LEAD + 4 sets covers all of it with room for a fling to overshoot by a
         frame or two before the scroll handler catches it.

         Worked out from the CARD COUNT rather than the measured widths, because
         the widths change at the 768px breakpoint (--rgproof-per goes 2.4 -> 3)
         and the clones are built once and kept. Three cards per view is the
         densest the CSS ever gets, so cards/3 sets is the narrowest a set can
         be against the track. Capped, in case somebody adds two blocks and a
         very wide screen asks for a row of forty. */
      function copiesNeeded() {
        return Math.max(5, Math.min(8, LEAD + 4 + Math.ceil(3 / cards.length)));
      }

      function build() {
        if (!(oneSet() > 0)) return false;

        var copies = copiesNeeded();
        var frag = document.createDocumentFragment();
        for (var c = 1; c < copies; c++) {
          for (var i = 0; i < cards.length; i++) {
            var clone = cards[i].cloneNode(true);
            /* Decorative repeats: announced once, by the originals. Nothing
               inside a card is focusable, so this hides nothing reachable. */
            clone.setAttribute('aria-hidden', 'true');
            clone.setAttribute('data-rgproof-clone', '');
            /* The section's blocks are addressed by these in the theme editor,
               and a duplicate there is worse than none. */
            clone.removeAttribute('data-shopify-editor-block');
            clone.removeAttribute('id');
            var shot = clone.querySelector('.rgpdp-gallery__slide');
            if (shot) shot.setAttribute('data-lb-clone', String(i));
            frag.appendChild(clone);
          }
        }
        strip.appendChild(frag);
        built = true;
        return true;
      }

      /* Maps any offset back into the window, always by a whole number of set
         widths - which is what makes the move invisible. */
      function wrap(v) {
        if (!(setW > 0)) return v;
        return v - Math.floor((v - lo) / setW) * setW;
      }

      /* Flagged, so the scroll this raises is not read back as the reader's. */
      function setScroll(v) {
        wrapping = true;
        try { track.scrollTo({ left: v, behavior: 'instant' }); }
        catch (e) { track.scrollLeft = v; }
        wrapping = false;
      }

      function measure() {
        if (!built && !build()) return;
        setW = oneSet();
        if (!(setW > 0)) return;
        lo = setW * LEAD;
        hi = lo + setW;
        var at = track.scrollLeft;
        if (at < lo || at >= hi) setScroll(wrap(at));
      }

      /* THE DRIFT, in one call. Concrete pixels rather than a stylesheet
         @keyframes carrying a var(): the distance is measured, and a keyframe
         with an unresolved custom property in it is the shape an engine is
         liable to decline to run on the compositor - which would put this
         straight back on the main thread the rewrite exists to get off.

         Rebuilt rather than adjusted when the width changes, because the
         distance is baked into the keyframes. The phase is carried across so a
         resize does not jog the strip back to the start of its cycle. */
      function retime() {
        if (!strip.animate) return;
        /* Only when the distance has actually changed. refresh() runs on every
           resize and on each measuring retry, and rebuilding an animation that
           is already correct would drop a frame for nothing. */
        if (anim && animW === setW) return;
        var dur = (setW / speed) * 1000;
        var phase = 0;
        if (anim) {
          var was = anim.effect && anim.effect.getTiming ? anim.effect.getTiming().duration : dur;
          var at = typeof anim.currentTime === 'number' ? anim.currentTime : 0;
          if (was > 0) phase = (at % was) / was;
          anim.cancel();
        }
        anim = strip.animate(
          [{ transform: 'translate3d(0,0,0)' }, { transform: 'translate3d(' + (-setW) + 'px,0,0)' }],
          { duration: dur, iterations: Infinity, easing: 'linear' }
        );
        animW = setW;
        try { anim.currentTime = phase * dur; } catch (e) {}
      }

      /* Paused, never cancelled, for everything except the reader's own motion
         setting: pausing holds the phase to the millisecond, so letting go of
         the strip - or closing the viewer over it - carries on from the same
         pixel. Cancelling would snap the transform back to zero, and the phase
         it was at is not a whole set, so that jump WOULD be visible. */
      function sync() {
        if (!anim) return;
        if (!seen || down || covered()) anim.pause(); else anim.play();
      }

      function refresh() {
        if (silenced()) {
          if (anim) { anim.cancel(); anim = null; }
          return;
        }
        measure();
        if (setW > 0) retime();
        sync();
      }

      /* The card widths come from CSS and do not depend on the photographs, so
         this is usually right on the first run even though every image is
         lazy-loaded and none have arrived. The retry is for the case where it
         is not - a webfont still swapping, a section above still settling -
         so a strip measured a moment too early heals itself instead of being
         dead for the life of the page. */
      function settle() {
        refresh();
        if (!(setW > 0) && !silenced() && tries++ < 20) setTimeout(settle, 250);
      }

      /* PRESS AND HOLD IS THE PAUSE. There is deliberately NO hover pause, and
         that is a fix rather than an omission: browsers fire pointerenter and
         pointerleave when the element under a STATIONARY cursor changes because
         the page scrolled, so a reader scrolling down to this band parks the
         cursor on the strip, `over` latches true, and the drift never runs at
         all. Holding is also the gesture that was asked for, and it is the one
         that cannot happen by accident. */
      function press(e) {
        down = true;
        dragged = false;
        /* A finger scrolls the track itself and gets the platform's momentum;
           a mouse has no such gesture over an overflow container, so a press
           and drag is turned into one here. pointerType is absent on the
           touchstart/mousedown fallback below, where mousedown means mouse. */
        dragging = !e || !e.pointerType || e.pointerType === 'mouse';
        if (dragging) {
          dragFrom = e ? e.clientX : 0;
          dragAt = track.scrollLeft;
        }
        sync();
      }

      function drag(e) {
        if (!dragging || !down || !(setW > 0)) return;
        var dx = e.clientX - dragFrom;
        if (!dragged && Math.abs(dx) < NUDGE) return;
        dragged = true;
        track.classList.add('is-dragging');
        if (e.cancelable) e.preventDefault();
        var to = dragAt - dx;
        /* Wrapped mid-drag, with the anchor moved by the same amount so the
           strip stays under the pointer. Without the re-base the wrap would
           read as the strip jumping out from under the mouse; with it, a mouse
           drag runs as far in either direction as anyone cares to go. */
        if (to <= lo - setW || to >= hi + setW) {
          var moved = wrap(to);
          dragAt += moved - to;
          to = moved;
        }
        setScroll(to);
      }

      function release() {
        if (!down) return;
        down = false;
        dragging = false;
        track.classList.remove('is-dragging');
        sync();
      }

      /* THE PRESS MUST NOT SWALLOW THE TAP. A press that never became a drag
         calls no preventDefault and stops nothing, so it goes on to raise click
         on the photo underneath - which is what initLightbox() binds, and how a
         tap still opens the viewer. A press that DID become a drag has to eat
         the click it ends with, or every drag would open a photograph; that is
         what this does, on the capture phase so it lands before the image's own
         handler rather than after it. */
      on(track, 'click', function (e) {
        if (!dragged) return;
        dragged = false;
        e.stopPropagation();
        e.preventDefault();
      }, true);

      on(track, 'dragstart', function (e) { if (dragging) e.preventDefault(); });

      if (window.PointerEvent) {
        on(track, 'pointerdown', press);
        /* Moved and released on the document, not the track. A drag that ends
           past the edge of the strip - or one the browser takes over to scroll
           with - delivers its pointerup somewhere else entirely, and a release
           that never arrives would leave the drift held down for the life of
           the page. The `down` guard makes every unrelated one a no-op. */
        on(document, 'pointermove', drag);
        on(document, 'pointerup', release);
        on(document, 'pointercancel', release);
      } else {
        on(track, 'touchstart', function () { press(null); dragging = false; }, { passive: true });
        on(document, 'touchend', release);
        on(document, 'touchcancel', release);
        on(track, 'mousedown', press);
        on(document, 'mousemove', drag);
        on(document, 'mouseup', release);
      }

      /* A WHOLE SET OF SLACK BEFORE ANYTHING MOVES. Wrapping the moment the
         offset leaves its window would snap the strip back in the middle of
         every other swipe, and a write to scrollLeft ends the platform's
         momentum. A set is more than a viewport wide, so an ordinary swipe
         never reaches this and a long one is caught within a frame - long
         before the copies run out. Never while a finger is down: the browser
         owns the offset then, and writing under it fights the drag. */
      on(track, 'scroll', function () {
        if (wrapping || down || !(setW > 0)) return;
        var at = track.scrollLeft;
        if (at > lo - setW && at < hi + setW) return;
        setScroll(wrap(at));
      }, { passive: true });

      /* Re-measures an already-built strip - the card widths move at the 768px
         breakpoint, and the animation is written in pixels - but never builds
         one. A strip that has not been measured yet, or that belongs to a
         reader who asked for reduced motion, has no business growing twenty
         extra cards because a window was dragged. */
      on(window, 'resize', debounce(function () { if (built) refresh(); }, 200), { passive: true });

      /* Reduced motion is answered live, not read once at load. A reader can
         flip the OS setting with the page open, and devtools' rendering panel
         can flip it under you and leave it that way - which is a long way to
         chase a strip that simply will not move. */
      if (reduceMotion.addEventListener) {
        reduceMotion.addEventListener('change', function () { tries = 0; settle(); });
      }

      /* There is no "lightbox closed" event to subscribe to, and no per-frame
         loop left to poll it from, so the viewer's own class is watched. */
      if (viewer && window.MutationObserver) {
        new MutationObserver(sync).observe(viewer, { attributes: true, attributeFilter: ['class'] });
      }

      /* Paused until the band is actually on screen. A paused animation costs
         nothing, and pausing rather than removing is what keeps the phase. */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            seen = entry.isIntersecting;
            sync();
          });
        }, { rootMargin: '80px 0px' }).observe(proof);
      } else {
        seen = true;
      }

      settle();

      /* One attribute, written last, so "did this run?" is a question the
         browser can answer from the elements panel or a one-line console read
         instead of from the network tab. It is the difference between a strip
         that is not moving and a file that never arrived. */
      proof.setAttribute('data-rgproof-ready', '');
    });
  })();

  /* ── Product image lightbox ────────────────────────────────────────────── */
  (function initLightbox() {
    /* The product gallery is the FIRST [data-gallery] on the page and always
       the buy box's. Other image groups - the Q&A carousel's slides - mark
       themselves [data-lightbox-group] instead and open the same viewer through
       openGroup(). They must not claim [data-gallery]: initGallery and this
       lookup would both find the replica section first, because it renders
       above the buy box on page.pdp-adv6.json. */
    var gallery = $('[data-gallery]');
    var lb = $('[data-lightbox]');
    if (!lb) return;

    var slides = gallery ? $$('.rgpdp-gallery__slide', gallery) : [];
    var otherGroups = $$('[data-lightbox-group]');
    if (!slides.length && !otherGroups.length) return;

    var lbStage  = $('[data-lb-stage]', lb);
    var lbTrack  = $('[data-lb-track]', lb);
    var lbImg    = $('[data-lb-img]', lb);
    var slideEls = $$('.rgpdp-lb__slide', lbTrack);
    var imgPrev  = slideEls[0] ? slideEls[0].querySelector('img') : null;
    var imgCurr  = lbImg;
    var imgNext  = slideEls[2] ? slideEls[2].querySelector('img') : null;
    var lbCounter = $('[data-lb-counter]', lb);
    var btnClose  = $('[data-lb-close]', lb);
    var btnZoom   = $('[data-lb-zoom]', lb);
    var btnPrev   = $('[data-lb-prev]', lb);
    var btnNext   = $('[data-lb-next]', lb);

    var SLIDE_GAP = 22;
    var group = slides.slice();
    var idx = 0;
    var zoomed = false;
    var ZOOM = 2.4, MAX_ZOOM = 4;
    var scale = 1;
    var pan = { x: 0, y: 0 };
    var stageW = 0;
    var animating = false;

    function measure() { stageW = lbStage ? lbStage.clientWidth : window.innerWidth; }
    function baseOffset() { return -(stageW + SLIDE_GAP); }
    function setTrack(x, animate) {
      if (!lbTrack) return;
      lbTrack.style.transition = animate ? 'transform 0.28s cubic-bezier(0.4,0,0.2,1)' : 'none';
      lbTrack.style.transform = 'translateX(' + x + 'px)';
    }
    function imgSrcFor(cell) {
      if (!cell) return '';
      var img = cell.querySelector('img');
      if (!img) return '';
      return img.getAttribute('data-full') || img.getAttribute('src');
    }
    function altFor(cell) {
      if (!cell) return '';
      var img = cell.querySelector('img');
      return img ? (img.getAttribute('alt') || '') : '';
    }
    function cellAt(i) {
      if (!group.length) return null;
      return group[((i % group.length) + group.length) % group.length];
    }
    function resetZoom() {
      zoomed = false; scale = 1; pan.x = 0; pan.y = 0;
      if (lbStage) lbStage.classList.remove('is-zoomed', 'is-dragging');
      if (lbImg) { lbImg.style.transition = 'transform 0.25s ease'; lbImg.style.transform = 'scale(1)'; }
    }
    function applyTransform() {
      if (!lbImg) return;
      zoomed = scale > 1.01;
      if (lbStage) lbStage.classList.toggle('is-zoomed', zoomed);
      lbImg.style.transform = 'translate(' + pan.x + 'px,' + pan.y + 'px) scale(' + scale + ')';
    }
    function fill(imgEl, cell) {
      if (!imgEl) return;
      var src = imgSrcFor(cell);
      imgEl.src = src || '';
      imgEl.alt = altFor(cell);
      imgEl.style.visibility = src ? 'visible' : 'hidden';
    }
    function render() {
      if (!group.length) return;
      idx = ((idx % group.length) + group.length) % group.length;
      measure();
      resetZoom();
      var single = group.length <= 1;
      fill(imgCurr, group[idx]);
      fill(imgPrev, single ? null : cellAt(idx - 1));
      fill(imgNext, single ? null : cellAt(idx + 1));
      setTrack(baseOffset(), false);
      if (lbCounter) lbCounter.textContent = (idx + 1) + ' / ' + group.length;
      if (btnPrev) btnPrev.style.display = single ? 'none' : '';
      if (btnNext) btnNext.style.display = single ? 'none' : '';
      [idx - 2, idx + 2].forEach(function (i) { var sx = imgSrcFor(cellAt(i)); if (sx) { var im = new Image(); im.src = sx; } });
    }
    function go(dir) {
      if (animating || group.length <= 1) return;
      animating = true;
      resetZoom();
      setTrack(baseOffset() - dir * (stageW + SLIDE_GAP), true);
      var done = false;
      function finish() {
        if (done) return; done = true;
        if (lbTrack) lbTrack.removeEventListener('transitionend', finish);
        idx += dir; render(); animating = false;
      }
      if (lbTrack) lbTrack.addEventListener('transitionend', finish);
      setTimeout(finish, 360);
    }
    function next() { go(1); }
    function prev() { go(-1); }

    /* Set while the viewer is up: the function that puts the page back under
       the photograph the reader opened. See pinTo(). */
    var unpin = null;

    function openGroup(cells, startIndex, opener) {
      if (!cells.length) return;
      /* Two opens without a close between them would take the reference-counted
         lock twice, and one close would then leave the page locked and the
         offset unrestored for good. Nothing on the page does that today; the
         guard is a lot cheaper than the bug is to find. */
      if (lb.classList.contains('is-open')) return;
      group = cells;
      idx = Math.max(0, Math.min(startIndex || 0, group.length - 1));
      /* Read BEFORE the lock, which is what re-lays the page out. */
      unpin = pinTo(opener || group[idx]);
      lb.classList.add('is-open');
      lb.setAttribute('aria-hidden', 'false');
      /* Uses the shared reference-counted lock rather than writing
         body.style.overflow directly. The cart drawer's overlay currently makes
         it impossible to open the lightbox on top of it, so the two cannot
         actually collide today - but two independent scroll-lock mechanisms in
         one file is exactly how that stops being true after a z-index change. */
      lockScroll(true);
      render();
      group.forEach(function (c) { var sx = imgSrcFor(c); if (sx) { var im = new Image(); im.src = sx; } });
    }
    function close() {
      lb.classList.remove('is-open');
      lb.setAttribute('aria-hidden', 'true');
      resetZoom();
      /* Offset first, then the photograph. lockScroll puts the number back,
         which is right to within whatever the page shifted by while it was
         covered; unpin corrects that remainder, and is the reason the reader
         comes back to the picture they left rather than to a screen a little
         above it. */
      lockScroll(false);
      if (unpin) { unpin(); unpin = null; }
    }
    function toggleZoom() {
      if (lbImg) lbImg.style.transition = 'transform 0.25s ease';
      scale = (scale > 1.01) ? 1 : ZOOM;
      pan.x = 0; pan.y = 0;
      applyTransform();
    }

    /* THE REPEATS DO NOT COUNT. initProofCarousels() clones its cards to make
       the strip loop, and every clone carries .rgpdp-gallery__slide like the
       original it came from. Left in, the viewer would count "1 / 16" over four
       photographs. A clone is marked [data-lb-clone="n"], where n is which of
       the originals it repeats, so it is dropped from the group here and read
       for its index when somebody taps one. */
    function realCells(container) {
      return $$('.rgpdp-gallery__slide', container).filter(function (c) {
        return !c.hasAttribute('data-lb-clone');
      });
    }

    /* Delegated rather than bound one photo at a time, because the photos this
       would bind are not the photos that will be on the page when somebody taps
       one: the product gallery's are rendered from product.media and a variant
       swap replaces them, and the proof strip appends its clones the first time
       it comes on screen. The cells are collected at tap time for the same
       reason. Only the picture and the zoom button open the viewer - clicking
       the letterboxed bars beside a testimonial photo does nothing, as before. */
    function bindGroup(container) {
      on(container, 'click', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        if (t.tagName !== 'IMG' && !t.closest('[data-gallery-zoom]')) return;
        var cell = t.closest('.rgpdp-gallery__slide');
        if (!cell || !container.contains(cell)) return;
        e.stopPropagation();
        var cells = realCells(container);
        var mark = cell.getAttribute('data-lb-clone');
        var at = mark === null ? cells.indexOf(cell) : parseInt(mark, 10);
        openGroup(cells, at > 0 ? at : 0, cell);
      });
    }

    if (gallery) bindGroup(gallery);
    otherGroups.forEach(bindGroup);

    on(btnClose, 'click', close);
    on(btnNext, 'click', function (e) { e.stopPropagation(); next(); });
    on(btnPrev, 'click', function (e) { e.stopPropagation(); prev(); });
    on(btnZoom, 'click', function (e) { e.stopPropagation(); toggleZoom(); });
    on(lb, 'click', function (e) { if (e.target === lb) close(); });
    on(window, 'resize', function () {
      if (lb.classList.contains('is-open') && !animating) { measure(); setTrack(baseOffset(), false); }
    });

    if (lbStage && window.PointerEvent) {
      var pointers = {};
      var pinchStart = null;
      var startG = null;
      var moved = false;
      var SWIPE_DIST = 50, TAP_MOVE = 8;
      var lastTap = 0;

      function pts() { return Object.keys(pointers).map(function (k) { return pointers[k]; }); }
      function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }

      lbStage.addEventListener('pointerdown', function (e) {
        if (animating) return;
        try { lbStage.setPointerCapture(e.pointerId); } catch (err) {}
        pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        if (lbImg) lbImg.style.transition = 'none';
        var p = pts();
        if (p.length === 2) {
          pinchStart = { dist: dist(p[0], p[1]) || 1, scale: scale };
          startG = null;
        } else if (p.length === 1) {
          startG = { x: e.clientX, y: e.clientY, t: Date.now(), panX: pan.x, panY: pan.y, type: e.pointerType, base: baseOffset() };
          moved = false;
          if (scale > 1.01 && lbStage) lbStage.classList.add('is-dragging');
        }
      });

      lbStage.addEventListener('pointermove', function (e) {
        if (!pointers[e.pointerId]) return;
        pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        var p = pts();
        if (pinchStart && p.length >= 2) {
          scale = Math.min(MAX_ZOOM, Math.max(1, pinchStart.scale * (dist(p[0], p[1]) / pinchStart.dist)));
          applyTransform();
          moved = true;
        } else if (startG && p.length === 1) {
          var dx = e.clientX - startG.x, dy = e.clientY - startG.y;
          if (Math.abs(dx) > TAP_MOVE || Math.abs(dy) > TAP_MOVE) moved = true;
          if (scale > 1.01) {
            pan.x = startG.panX + dx; pan.y = startG.panY + dy;
            applyTransform();
          } else {
            setTrack(startG.base + dx, false);
          }
        }
      });

      function endPointer(e) {
        var hadStart = startG, hadPinch = pinchStart;
        delete pointers[e.pointerId];
        var remaining = pts().length;

        if (hadPinch && remaining < 2) {
          pinchStart = null;
          if (lbImg) lbImg.style.transition = 'transform 0.22s ease';
          if (scale <= 1.01) resetZoom();
          startG = null;
          return;
        }
        if (hadStart && remaining === 0) {
          if (lbStage) lbStage.classList.remove('is-dragging');
          var dx = e.clientX - hadStart.x, dy = e.clientY - hadStart.y;
          var dt = Date.now() - hadStart.t;
          startG = null;

          if (!moved && dt < 350) {
            var onImg = (e.target === imgCurr);
            if (hadStart.type === 'mouse') {
              if (onImg) toggleZoom(); else close();
            } else {
              var now = Date.now();
              if (now - lastTap < 300) { toggleZoom(); lastTap = 0; }
              else { lastTap = now; }
            }
            return;
          }
          if (scale > 1.01) { applyTransform(); return; }

          if (Math.abs(dx) > SWIPE_DIST && Math.abs(dx) > Math.abs(dy) && group.length > 1) {
            go(dx < 0 ? 1 : -1);
          } else {
            setTrack(baseOffset(), true);
          }
        }
      }
      lbStage.addEventListener('pointerup', endPointer);
      lbStage.addEventListener('pointercancel', function (e) {
        delete pointers[e.pointerId];
        pinchStart = null; startG = null;
        if (lbStage) lbStage.classList.remove('is-dragging');
        if (scale <= 1.01) setTrack(baseOffset(), true); else applyTransform();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (!lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    });
  })();


  /* ── Photo review wall ────────────────────────────────────────────────────
     Everything interactive in 1-custom-pdp-photo-wall, in four parts: the two
     panels that drop out of the summary bar, the sort, the review viewer and
     the write-a-review dialog. Each part is guarded on its own markup, so a
     wall rendered without a viewer, or without the dialog, still gets the rest.

     ONE FUNCTION AND NOT FOUR because they are not independent. The verified
     note lives inside the viewer and has to close when it does; the dialog has
     to shut the panels before it covers them; all three overlays share one
     reference-counted scroll lock. Split them up and those handoffs become
     cross-component events for no gain - there is one section here, not four.

     SCOPED TO [data-rgwall], the wrapper the section puts around the wall and
     its overlays together, so two walls on one page cannot cross-wire.
  */
  (function initPhotoWall() {
    $$('[data-rgwall]').forEach(function (wall) {
      var lb = $('[data-rgwall-lb]', wall);
      var cards = $$('[data-rgwall-card]', wall);

      /* ── Panels in the summary bar ───────────────────────────────────────
         Two of them - the ratings breakdown and the sort menu - and they are
         mutually exclusive, close on Escape and close on a click anywhere else.
         All of that is one pair of functions rather than two of everything,
         because the only difference between them is which button they belong
         to. */
      var panels = [];

      function registerPanel(btn, panel) {
        if (!btn || !panel) return null;
        var entry = { btn: btn, panel: panel };
        panels.push(entry);
        on(btn, 'click', function () { togglePanel(entry, panel.hidden); });
        return entry;
      }

      function togglePanel(entry, open) {
        panels.forEach(function (p) {
          var want = (p === entry) && open;
          p.panel.hidden = !want;
          p.btn.setAttribute('aria-expanded', want ? 'true' : 'false');
        });
      }

      function closePanels() { togglePanel(null, false); }

      registerPanel($('[data-rgwall-dist-toggle]', wall), $('[data-rgwall-dist]', wall));
      registerPanel($('[data-rgwall-sort-toggle]', wall), $('[data-rgwall-sort-menu]', wall));

      var infoBtn = $('[data-rgwall-info-toggle]', wall);
      var infoNote = $('[data-rgwall-info]', wall);
      var info = registerPanel(infoBtn, infoNote);

      if (panels.length) {
        /* pointerdown, not click: a menu that closes on click would be re-opened
           by the very click that reached its own button, because the button's
           own handler runs after this one. Reading the down event instead lets
           the button's click land on a panel this has already closed, which is
           the toggle the reader expects. */
        on(document, 'pointerdown', function (e) {
          var live = panels.filter(function (p) { return !p.panel.hidden; });
          if (!live.length) return;
          var inside = live.some(function (p) {
            return p.panel.contains(e.target) || p.btn.contains(e.target);
          });
          if (!inside) closePanels();
        });
        on(document, 'keydown', function (e) {
          if (e.key !== 'Escape') return;
          if (panels.some(function (p) { return !p.panel.hidden; })) closePanels();
        });
      }

      /* ── Order, layout and paging ────────────────────────────────────────
         Three things that all end in the same place - which cards are in which
         column, in what order - so they are one block with one entry point.
         `order` is the current sequence, `shown` is how much of it is on the
         page, and layout() is the only thing that touches the DOM.

         THE WALL READS ACROSS, NOT DOWN, AND THAT IS WHY THIS EXISTS. The
         stylesheet lays the cards out with column-count, which is free and
         needs no script - but multicol fills column one to the bottom before it
         starts column two, so review two ends up UNDER review one instead of
         beside it. Sorted by Featured that puts the five reviews with photos in
         a vertical stripe down the left. So the script takes the run apart and
         rebuilds it as real columns, placing each card in whichever is
         currently shortest: review one goes left, review two next to it, and
         the bottom edge still comes out roughly level.

         HEIGHTS ARE MEASURED ONCE, IN A BATCH. Every card is parked in column
         one first - which is already the exact width it will end up at, since
         the columns are equal - then every offsetHeight is read in one pass.
         The first read flushes layout and the rest come out of the same cached
         box, so this is two reflows for the whole wall rather than one per
         card. Placing them afterwards is arithmetic on numbers already in hand.

         DOCUMENT ORDER IS THE FEATURED ORDER. The section prints the featured
         reviews first and the rest after, both newest first, so the default
         sort needs no comparator - it is the array as read. The other three
         sort a COPY, so Featured can always be got back exactly rather than
         approximated by sorting on a flag. */
      var grid = $('[data-rgwall-grid]', wall);
      var sortItems = $$('[data-rgwall-sort]', wall);
      var moreWrap = $('[data-rgwall-more]', wall);
      var moreBtn = $('[data-rgwall-more-btn]', wall);

      function attr(el, name) { return el.getAttribute(name) || ''; }
      function newestFirst(a, b) { return attr(b, 'data-sort-date').localeCompare(attr(a, 'data-sort-date')); }
      function stars(el) { return parseInt(attr(el, 'data-sort-rating'), 10) || 0; }

      var SORTS = {
        featured: null,
        newest: newestFirst,
        highest: function (a, b) { return stars(b) - stars(a) || newestFirst(a, b); },
        lowest: function (a, b) { return stars(a) - stars(b) || newestFirst(a, b); }
      };

      if (grid && cards.length) {
        var natural = cards.slice();
        var order = natural.slice();
        var page = num(attr(grid, 'data-rgwall-page'), 20);
        if (page < 1) page = cards.length;
        var shown = Math.min(page, cards.length);
        var cols = [];
        var lastCols = 0;
        var lastWidth = 0;

        /* Read off the stylesheet rather than repeated here: --rgwall-cols is
           set at 360/520/960 beside the other rules those widths change, and
           this is the only thing that needs the number. */
        function colCount() {
          var raw = parseInt(window.getComputedStyle(grid).getPropertyValue('--rgwall-cols'), 10);
          return raw > 0 ? raw : 1;
        }

        function gapPx() {
          var raw = parseFloat(window.getComputedStyle(grid).getPropertyValue('--rgwall-gap'));
          return isNaN(raw) ? 12 : raw;
        }

        function buildCols(n) {
          if (cols.length === n) {
            cols.forEach(function (col) { col.textContent = ''; });
            return;
          }
          grid.textContent = '';
          cols = [];
          for (var i = 0; i < n; i++) {
            var col = document.createElement('div');
            col.className = 'rgwall__col';
            grid.appendChild(col);
            cols.push(col);
          }
        }

        function layout() {
          var n = colCount();
          /* The class is what turns the multicol layout off. Added on the first
             pass and never removed: from here on the columns are the layout. */
          grid.classList.add('rgwall__grid--js');
          buildCols(n);

          var live = [];
          var i;
          for (i = 0; i < order.length; i++) {
            if (!order[i].hidden) live.push(order[i]);
          }

          /* Park everything in column one at its final width, in one write. */
          var park = document.createDocumentFragment();
          for (i = 0; i < live.length; i++) park.appendChild(live[i]);
          cols[0].appendChild(park);

          var heights = [];
          for (i = 0; i < live.length; i++) heights.push(live[i].offsetHeight);

          var gap = gapPx();
          var sums = [];
          var buckets = [];
          for (i = 0; i < n; i++) { sums.push(0); buckets.push([]); }

          for (i = 0; i < live.length; i++) {
            var pick = 0;
            for (var j = 1; j < n; j++) {
              if (sums[j] < sums[pick]) pick = j;
            }
            buckets[pick].push(live[i]);
            sums[pick] += heights[i] + gap;
          }

          for (i = 0; i < n; i++) {
            var frag = document.createDocumentFragment();
            for (var k = 0; k < buckets[i].length; k++) frag.appendChild(buckets[i][k]);
            cols[i].appendChild(frag);
          }

          /* The reviews past the fold have to live somewhere, and column one is
             as good as anywhere: they are display:none, so they add no height
             and take no place in the packing above. */
          var rest = document.createDocumentFragment();
          for (i = 0; i < order.length; i++) {
            if (order[i].hidden) rest.appendChild(order[i]);
          }
          cols[0].appendChild(rest);

          lastCols = n;
          lastWidth = grid.clientWidth;
        }

        function paintPaging() {
          for (var i = 0; i < order.length; i++) order[i].hidden = i >= shown;
          if (moreWrap) moreWrap.hidden = shown >= order.length;
        }

        function applySort(key) {
          var cmp = SORTS[key];
          order = natural.slice();
          if (cmp) order.sort(cmp);
          paintPaging();
          layout();
        }

        sortItems.forEach(function (item) {
          on(item, 'click', function () {
            sortItems.forEach(function (other) {
              other.setAttribute('aria-pressed', other === item ? 'true' : 'false');
            });
            applySort(attr(item, 'data-rgwall-sort'));
            closePanels();
          });
        });

        on(moreBtn, 'click', function () {
          /* Read BEFORE the count moves. The last press adds whatever is left,
             which is usually fewer than a full page, so `shown - page` would
             point at a review that was already on screen. */
          var wasShowing = shown;
          shown = Math.min(shown + page, order.length);
          paintPaging();
          layout();
          /* Focus follows the reveal, so a keyboard reader is not stranded on a
             button that has just disappeared. */
          var first = order[wasShowing];
          var hit = first && first.querySelector('[data-rgwall-open]');
          if (hit) hit.focus({ preventScroll: true });
        });

        paintPaging();
        layout();

        /* Card heights change with column width, not only with column COUNT -
           a narrower column wraps the comment onto more lines - so any width
           change re-packs. Height changes do not, which is why this reads
           clientWidth rather than trusting the resize event on its own. */
        on(window, 'resize', debounce(function () {
          if (grid.clientWidth === lastWidth && colCount() === lastCols) return;
          layout();
        }, 150));

        /* A web font landing after first paint re-wraps every comment and makes
           every measurement above stale. Cheap to re-run, and does nothing on a
           browser without the promise. */
        if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
          document.fonts.ready.then(layout);
        }
      }

      /* ── The review viewer ───────────────────────────────────────────────
         Opening a card hands its full comment and its UNCROPPED photograph to
         the overlay the section renders beside the wall.

         DELIBERATELY NOT initLightbox(). That viewer is a track of three slides
         with a counter, pinch and zoom, and it shows photographs and nothing
         else; what a review has to open into is a photograph AND its text.

         NOTHING IS DUPLICATED INTO data-* ATTRIBUTES. Every field is read back
         off the card that was clicked - the name out of .rgwall__name, the
         rating by counting .is-on stars, the comment out of the <template> the
         snippet leaves behind - so there is one copy of each review on the page
         and it is the one the reader can already see. The three data-sort-*
         attributes are the exception and they are sort keys, not content.

         WITH THIS DEAD the wall still renders, still reads and still shows 500
         characters of every comment. Only the full text and the uncropped
         photograph go missing, which is the failure mode a review wall can
         afford. */
      if (lb && cards.length) {
        var elMedia = $('[data-rgwall-lb-media]', lb);
        var elBlur = $('[data-rgwall-lb-blur]', lb);
        var elImg = $('[data-rgwall-lb-img]', lb);
        var elName = $('[data-rgwall-lb-name]', lb);
        var elVerified = $('[data-rgwall-lb-verified]', lb);
        var elVlabel = $('[data-rgwall-lb-vlabel]', lb);
        var elStars = $$('[data-rgwall-lb-stars] .rgwall__star', lb);
        var elSrStars = $('[data-rgwall-lb-srstars]', lb);
        var elDate = $('[data-rgwall-lb-date]', lb);
        var elQuote = $('[data-rgwall-lb-quote]', lb);
        var btnClose = $('[data-rgwall-lb-close]', lb);

        var baseLabel = lb.getAttribute('aria-label') || 'Customer review';
        var opener = null;
        var unpin = null;

        var text = function (el, value) { if (el) el.textContent = value || ''; };

        var fill = function (card) {
          var img = card.querySelector('.rgwall__img');
          var full = img ? img.getAttribute('data-full') : '';
          var blur = img ? img.getAttribute('data-blur') : '';

          if (elMedia) elMedia.hidden = !full;
          /* removeAttribute and not src = '': an empty src resolves against the
             document URL, which fetches the page again as an image. */
          if (elImg) {
            if (full) elImg.src = full; else elImg.removeAttribute('src');
            elImg.alt = img ? (img.getAttribute('alt') || '') : '';
          }
          /* Longhand, to match the longhands the stylesheet sets around it. */
          if (elBlur) elBlur.style.backgroundImage = blur ? 'url("' + blur + '")' : '';

          var nameEl = card.querySelector('.rgwall__name');
          var name = nameEl ? nameEl.textContent.trim() : '';
          text(elName, name);
          lb.setAttribute('aria-label', name ? 'Review by ' + name : baseLabel);

          var vlabel = card.querySelector('.rgwall__vlabel');
          if (elVerified) elVerified.hidden = !vlabel;
          text(elVlabel, vlabel ? vlabel.textContent.trim() : '');

          var dateEl = card.querySelector('.rgwall__date');
          var date = dateEl ? dateEl.textContent.trim() : '';
          if (elDate) elDate.hidden = !date;
          text(elDate, date);

          var rating = $$('.rgwall__star.is-on', card).length;
          elStars.forEach(function (star, i) { star.classList.toggle('is-on', i < rating); });
          text(elSrStars, 'Rated ' + rating + ' out of 5');

          /* innerHTML and not textContent: the comment is written with &rsquo;
             and &amp; entities and newline_to_br has already turned its line
             breaks into <br>, so this is moving markup this theme rendered, not
             accepting any. A <template>'s contents are inert until read, which
             is why the long copy can sit in the card without being a second,
             visible copy of the review. */
          var tpl = card.querySelector('[data-rgwall-full]');
          if (elQuote) elQuote.innerHTML = tpl ? tpl.innerHTML : '';
        };

        var lbFocusables = function () {
          return $$('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', lb)
            .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
        };

        var openLb = function (card) {
          if (lb.classList.contains('is-open')) return;
          fill(card);
          opener = card.querySelector('[data-rgwall-open]');
          /* Read BEFORE the lock, which is what re-lays the page out. Same
             pair, same order, as initLightbox's openGroup/close. */
          unpin = pinTo(card);
          lb.classList.add('is-open');
          lb.setAttribute('aria-hidden', 'false');
          lockScroll(true);
          if (btnClose) btnClose.focus();
        };

        var closeLb = function () {
          if (!lb.classList.contains('is-open')) return;
          /* The note belongs to the review that is on screen, so it goes with
             it. Left open it would be the previous reviewer's popover hanging
             over the next one. */
          if (info) togglePanel(null, false);
          lb.classList.remove('is-open');
          lb.setAttribute('aria-hidden', 'true');
          lockScroll(false);
          if (unpin) { unpin(); unpin = null; }
          /* Last, and asking not to scroll. Focusing an element is allowed to
             scroll it into view, and a browser that does so here would undo the
             two lines above it - which is also why this cannot move up. */
          if (opener) { opener.focus({ preventScroll: true }); opener = null; }
        };

        cards.forEach(function (card) {
          on(card.querySelector('[data-rgwall-open]'), 'click', function () { openLb(card); });
        });

        /* THE CLICK HAS TO BEGIN ON THE BACKDROP AS WELL AS END THERE. A click
           event fires on the nearest ancestor of where the pointer went down
           and where it came up, so a text selection dragged out of a long
           comment and released over the dark surround arrives here with the
           overlay as its target and would otherwise shut the review the reader
           was mid-way through copying. The down flag is the whole fix. */
        var lbDown = false;
        on(lb, 'pointerdown', function (e) { lbDown = e.target === lb; });
        on(lb, 'click', function (e) { if (e.target === lb && lbDown) closeLb(); });
        on(btnClose, 'click', closeLb);

        on(document, 'keydown', function (e) {
          if (!lb.classList.contains('is-open')) return;
          /* The note is inside the viewer and shallower than it, so Escape has
             to take it off first or there is no way to dismiss it without
             closing the review as well. */
          if (e.key === 'Escape') {
            if (info && !infoNote.hidden) { togglePanel(null, false); infoBtn.focus(); return; }
            closeLb();
            return;
          }
          if (e.key !== 'Tab') return;

          var list = lbFocusables();
          if (!list.length) { e.preventDefault(); return; }
          var first = list[0];
          var last = list[list.length - 1];
          var here = document.activeElement;
          if (!lb.contains(here)) { e.preventDefault(); first.focus(); return; }
          if (e.shiftKey && here === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && here === last) { e.preventDefault(); first.focus(); }
        });
      }

      /* ── Write a review ──────────────────────────────────────────────────
         Three steps in one dialog, all of them in the DOM at once so the slide
         has something to slide. cur is the whole state; every other function
         reads it and paint() is the only one that writes to the chrome.

         EVERY MOVE GOES THROUGH busy(), by request: a veil with a spinner for a
         beat, then the move. On the last step that beat is a real network wait
         and the veil is honest; on the other two it is decoration, and the one
         constant below is where to change or remove it.

         THE DIALOG IS JAVASCRIPT OR NOTHING. It is opened by a button this file
         binds and every step but the first is hidden by the stylesheet, so with
         scripts off it is simply never reachable - which is the coherent
         outcome, and the reason there is no half-working fallback form. */
      var wr = $('[data-rgwr]', wall);
      var writeBtn = $('[data-rgwall-write]', wall);

      if (wr && writeBtn) {
        var WR_BEAT = 380;
        var WR_SLIDE = 340;

        var form = $('form', wr);
        var stage = $('[data-rgwr-stage]', wr);
        var foot = $('[data-rgwr-foot]', wr);
        var veil = $('[data-rgwr-loading]', wr);
        var btnBack = $('[data-rgwr-back]', wr);
        var btnNext = $('[data-rgwr-next]', wr);
        var btnDone = $('[data-rgwr-done]', wr);
        var btnShut = $('[data-rgwr-close]', wr);
        var bars = $$('[data-rgwr-bars] .rgwr__bar', wr);
        var starBtns = $$('[data-rgwr-star]', wr);
        var ratingInput = $('[data-rgwr-rating]', wr);
        var bodyInput = $('[data-rgwr-body]', wr);
        var firstInput = $('[data-rgwr-first]', wr);
        var emailInput = $('[data-rgwr-email]', wr);
        var tones = $$('[data-rgwr-tone]', wr);

        var steps = {};
        $$('[data-rgwr-step]', wr).forEach(function (el) { steps[el.getAttribute('data-rgwr-step')] = el; });

        var ORDER = ['1', '2', '3'];
        var cur = '1';
        var rating = 0;
        var beat = null;
        var wrOpener = null;

        function busy(fn) {
          if (beat) return;
          if (veil) veil.hidden = false;
          beat = window.setTimeout(function () {
            beat = null;
            if (veil) veil.hidden = true;
            fn();
          }, WR_BEAT);
        }

        function paint() {
          var reached = { '1': 1, '2': 2, '3': 3, 'done': 3 }[cur] || 1;
          if (foot) foot.hidden = (cur === '1' || cur === 'done');
          bars.forEach(function (bar, i) { bar.classList.toggle('is-on', i < reached); });
          if (btnNext) btnNext.hidden = (cur !== '2');
          if (btnDone) btnDone.hidden = (cur !== '3');
          validate();
        }

        function validate() {
          if (btnNext && cur === '2') {
            btnNext.disabled = !bodyInput || bodyInput.value.trim().length < 1;
          }
          if (btnDone && cur === '3') {
            var ok = firstInput && emailInput &&
                     firstInput.value.trim().length > 0 &&
                     emailInput.value.trim().length > 0;
            btnDone.disabled = !ok;
          }
        }

        function go(next, dir) {
          var from = steps[cur];
          var to = steps[next];
          if (!from || !to || next === cur) return;
          busy(function () {
            if (stage) stage.classList.toggle('is-back', dir < 0);
            from.classList.remove('is-active');
            from.classList.add('is-leaving');
            to.classList.add('is-active');
            /* The leaving panel keeps its visibility for the length of the
               slide and no longer; dropping the class is what parks it back
               off-stage for next time. */
            window.setTimeout(function () { from.classList.remove('is-leaving'); }, WR_SLIDE);
            cur = next;
            paint();
            var focusable = to.querySelector('textarea, input, button');
            if (focusable) focusable.focus({ preventScroll: true });
          });
        }

        /* Which of the three wordings step two wears. All three are rendered
           and hidden rather than one being written in by script, so the copy
           stays in the section's schema with its entities intact and nothing
           here has to know how to escape it. */
        function setTone() {
          var want = 'mid';
          if (rating > 0 && rating <= 2) want = 'low';
          else if (rating >= 4) want = 'high';
          tones.forEach(function (el) { el.hidden = el.getAttribute('data-rgwr-tone') !== want; });
        }

        function setRating(value) {
          rating = value;
          starBtns.forEach(function (star, i) {
            star.classList.toggle('is-on', i < value);
            star.setAttribute('aria-pressed', i < value ? 'true' : 'false');
          });
          if (ratingInput) ratingInput.value = value ? value + ' / 5' : '';
          setTone();
        }

        function reset() {
          setRating(0);
          if (bodyInput) bodyInput.value = '';
          if (firstInput) firstInput.value = '';
          if (emailInput) emailInput.value = '';
          $$('[data-rgwr-step]', wr).forEach(function (el) {
            el.classList.remove('is-active', 'is-leaving');
          });
          steps['1'].classList.add('is-active');
          cur = '1';
          paint();
        }

        function openWr() {
          if (wr.classList.contains('is-open')) return;
          closePanels();
          if (cur === 'done') reset();
          wrOpener = document.activeElement;
          wr.classList.add('is-open');
          wr.setAttribute('aria-hidden', 'false');
          lockScroll(true);
          if (btnShut) btnShut.focus();
        }

        function closeWr() {
          if (!wr.classList.contains('is-open')) return;
          wr.classList.remove('is-open');
          wr.setAttribute('aria-hidden', 'true');
          lockScroll(false);
          if (beat) { window.clearTimeout(beat); beat = null; if (veil) veil.hidden = true; }
          if (wrOpener && wrOpener.focus) { wrOpener.focus({ preventScroll: true }); }
          wrOpener = null;
        }

        starBtns.forEach(function (star, i) {
          on(star, 'click', function () {
            setRating(i + 1);
            go('2', 1);
          });
        });

        on(btnNext, 'click', function () { if (!btnNext.disabled) go('3', 1); });
        on(btnBack, 'click', function () {
          var at = ORDER.indexOf(cur);
          if (at > 0) go(ORDER[at - 1], -1);
        });
        on(bodyInput, 'input', validate);
        on(firstInput, 'input', validate);
        on(emailInput, 'input', validate);

        /* THE ONE PLACE THE REVIEW LEAVES THE PAGE. It is a real form with a
           real action, and this only stops the navigation the browser would do
           on its own so the reader gets the thank-you step instead of the whole
           page reloading. Any failure hands it straight back to the browser -
           a review that submits the slow way beats a review that is lost. */
        on(form, 'submit', function (e) {
          if (btnDone && btnDone.disabled) { e.preventDefault(); return; }
          if (!window.fetch || !window.FormData) return;
          e.preventDefault();
          if (veil) veil.hidden = false;
          fetch(form.getAttribute('action'), {
            method: 'POST',
            body: new FormData(form),
            headers: { Accept: 'text/html' }
          }).then(function () {
            if (veil) veil.hidden = true;
            go('done', 1);
          }).catch(function () {
            if (veil) veil.hidden = true;
            form.submit();
          });
        });

        on(writeBtn, 'click', openWr);
        on(btnShut, 'click', closeWr);

        var wrDown = false;
        on(wr, 'pointerdown', function (e) { wrDown = e.target === wr; });
        on(wr, 'click', function (e) { if (e.target === wr && wrDown) closeWr(); });

        on(document, 'keydown', function (e) {
          if (!wr.classList.contains('is-open')) return;
          if (e.key === 'Escape') { closeWr(); return; }
          if (e.key !== 'Tab') return;
          var list = $$('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', wr)
            .filter(function (el) { return !el.disabled && el.offsetParent !== null; });
          if (!list.length) { e.preventDefault(); return; }
          var first = list[0];
          var last = list[list.length - 1];
          var here = document.activeElement;
          if (!wr.contains(here)) { e.preventDefault(); first.focus(); return; }
          if (e.shiftKey && here === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && here === last) { e.preventDefault(); first.focus(); }
        });

        paint();
      }
    });
  })();

  /* ── Buy block: 3 one-time offers, sticky mirror, add-to-cart ────────────
     ONE-TIME PURCHASE ONLY. Everything to do with Subscribe & Save - the mode
     toggle, the TOGGLES map, the selling-plan reconciliation and the
     selling_plan sent on add-to-cart - is commented out below and marked
     "SUBSCRIPTION (PARKED)". The matching parked code is in
     1-custom-pdp-offers.liquid, 1-custom-pdp-hero.liquid and
     1-custom-pdp.css; re-enable all four together or none. */
  (function initBuyBox() {
    var block = $('[data-buyblock]');
    if (!block) return;

    var offers = $$('[data-offer]', block);
    if (!offers.length) return;

    /* SUBSCRIPTION (PARKED)
    var modeToggle = $('[data-mode-toggle]', block);
    var modeToggleLabel = $('[data-mode-toggle-label]', block);
    */
    var modeTitle = $('[data-mode-title]', block);
    var addBtn = $('[data-add-to-cart]', block);

    /* Gifts unlocked panel, on templates that turn show_gifts on. Each gift
       opens once the selected tile ships at least data-gift-unlock bottles -
       see the comment above it in 1-custom-pdp-hero.liquid. */
    var gifts = $$('[data-gift]', block);

    var stickyName = $('[data-sticky-name]');
    var stickyPrice = $('[data-sticky-price]');
    var stickyWas = $('[data-sticky-was]');

    /* The price pair ON the add-to-cart button. Scoped to `block` on purpose:
       the sticky add-to-cart in 1-custom-pdp-sticky-atc carries the same
       .rgpdp-addcart class and the same label/spinner pair, but no price of its
       own - it sits beside .rgpdp-sticky__price, which is what the three
       sticky* lookups above already feed. A document-wide query here would be
       the start of the two disagreeing. */
    var atcNow = $('[data-atc-now]', block);
    var atcWas = $('[data-atc-was]', block);

    /* TITLES.once must match the data-mode-title string rendered by
       1-custom-pdp-hero.liquid, or the heading changes on load.
       SUBSCRIPTION (PARKED): sub: 'Subscribe & Save (Auto-Delivery)' and
       var TOGGLES = { sub: 'BUY ONCE (ONE-TIME PURCHASE)', once: 'SUBSCRIBE & SAVE MORE' }; */
    var TITLES = { once: 'One-Time Purchase Only' };

    /* units '3' is the Buy 2 Get 1 Free tile - three bottles - which is the one
       the Liquid renders checked. data-units is a BOTTLE COUNT, not a quantity;
       it is unique per tile (1 / 3 / 5) and that is the only property this
       lookup needs. Change both together. */
    var state = { mode: 'once', units: '3' };
    /* The block-level variant is the FALLBACK only. Each tile carries its own
       data-variant-id and that is what add-to-cart posts - see buyNow. */
    var variantId = block.getAttribute('data-variant-id') || '';
    var handle = block.getAttribute('data-product-handle') || 'regalia-bakuchiol-anti-aging-serum';
    /* SUBSCRIPTION (PARKED) - plan-key -> selling-plan id. Stays empty while the
       sub tiles are parked; kept so the parked code below still reads. */
    var planByKey = {};

    /* SUBSCRIPTION (PARKED) - harvest the Liquid-rendered plan ids off the sub
       tiles. There are no data-mode="sub" tiles while we are one-time only.
    offers.forEach(function (o) {
      if (o.getAttribute('data-mode') !== 'sub') return;
      var k = o.getAttribute('data-plan-key');
      var pid = o.getAttribute('data-selling-plan');
      if (k && pid) planByKey[k] = pid;
    });
    */

    /* ── Assurance ticks under the button (PARKED) ─────────────────────────
       The [data-assure3] list this rebuilt on every selection change is gone
       from 1-custom-pdp-hero.liquid, replaced by the static .rgpdp-trustbox
       card. Nothing under the button varies by offer any more - free shipping
       and duties-included hold for all three tiers - so the list had become a
       redraw that always produced the same two rows.

       Parked rather than deleted because the SUBSCRIPTION work it was built for
       is parked too: a sub tile needs its cadence row ("Auto-delivers every N
       days") and a cancel-any-time row, and those DO vary by selection. Bring
       this back with the rest of the subscription code, and give it a list to
       write into again - the markup went with the old ticks.

       Two things it did that a rewrite must keep. The tick icon was cloned out
       of the Liquid-rendered first <li> rather than hard-coded here, so the SVG
       had exactly one definition on the page; and it was captured BEFORE the
       first render, because rendering replaced the innerHTML it came from. The
       cadence came off the selected tile's data-freq, never a lookup table in
       here - an ASSURE_DAYS map once had the tile saying "every 12 weeks" (84
       days) while this list said "every 90 days", which for a recurring charge
       is a statement about billing frequency, not a cosmetic slip.

    var assureList = $('[data-assure3]', block);
    var assureTick = '';
    if (assureList) {
      var firstTick = $('.rgpdp-assure3__tick', assureList);
      assureTick = firstTick ? firstTick.outerHTML : '';
    }

    function renderAssure() {
      if (!assureList) return;
      var rows = [];
      var o = currentOffer();
      var freq = o ? (o.getAttribute('data-freq') || '') : '';
      if (state.mode === 'sub' && freq) rows.push(freq.replace(/^Auto-ships/i, 'Auto-delivers'));
      rows.push('FREE shipping');
      rows.push('Duties &amp; Taxes Included');
      if (state.mode === 'sub') rows.push('Cancel or change any time');
      assureList.innerHTML = rows.map(function (r) { return '<li>' + assureTick + r + '</li>'; }).join('');
    }
    */

    function offerFor(mode, units) {
      for (var i = 0; i < offers.length; i++) {
        if (offers[i].getAttribute('data-mode') === mode &&
            offers[i].getAttribute('data-units') === units) return offers[i];
      }
      return null;
    }
    function currentOffer() { return offerFor(state.mode, state.units); }

    function selectOffer(o) {
      if (!o) return;
      state.mode = o.getAttribute('data-mode');
      state.units = o.getAttribute('data-units');
      var input = $('[data-offer-input]', o);
      if (input) input.checked = true;
      offers.forEach(function (x) { x.classList.toggle('is-selected', x === o); });

      var units = parseInt(state.units, 10) || 0;
      gifts.forEach(function (g) {
        g.classList.toggle('is-unlocked', units >= parseInt(g.getAttribute('data-gift-unlock'), 10));
      });

      var d = o.dataset;
      if (stickyName && d.name) stickyName.textContent = d.name;
      if (stickyPrice && d.price) stickyPrice.textContent = d.price;
      if (stickyWas) {
        if (d.was) { stickyWas.textContent = d.was; stickyWas.hidden = false; }
        else { stickyWas.hidden = true; }
      }

      /* Same two figures again, on the button. The strike is dropped when there
         is nothing to strike - a tier whose "was" equals its "now" would
         otherwise put a crossed-out copy of the price the reader is being
         charged right next to it, which reads as an error. */
      if (atcNow && d.price) atcNow.textContent = d.price;
      if (atcWas) {
        if (d.was && d.was !== d.price) { atcWas.textContent = d.was; atcWas.hidden = false; }
        else { atcWas.hidden = true; }
      }

      /* SUBSCRIPTION (PARKED) - renderAssure(); see the block above. Nothing
         under the button redraws on selection while we are one-time only. */
    }

    /* Kept as a function even with one mode left, so the parked toggle has
       something to call again and the heading has one writer. */
    function setMode(mode) {
      state.mode = mode;
      block.classList.toggle('is-once', mode === 'once');
      if (modeTitle) modeTitle.textContent = TITLES[mode];
      /* SUBSCRIPTION (PARKED)
      if (modeToggleLabel) modeToggleLabel.textContent = TOGGLES[mode];
      */
      selectOffer(offerFor(mode, state.units) || offerFor(mode, '3') || offerFor(mode, '1'));
    }

    offers.forEach(function (o) {
      var input = $('[data-offer-input]', o);
      on(input, 'change', function () { if (input.checked) selectOffer(o); });
    });

    /* SUBSCRIPTION (PARKED)
    if (modeToggle) {
      on(modeToggle, 'click', function () { setMode(state.mode === 'sub' ? 'once' : 'sub'); });
    }
    */

    setMode('once');

    /* ── Variant reconciliation ────────────────────────────────────────── */
    /* SUBSCRIPTION (PARKED) - writes a reconciled selling-plan id back onto the
       matching sub tile. Nothing calls it while there are no sub tiles.
    function applyPlan(key, id, force) {
      if (!key || !id) return;
      if (!force && planByKey[key]) return;
      planByKey[key] = String(id);
      offers.forEach(function (o) {
        if (o.getAttribute('data-mode') === 'sub' && o.getAttribute('data-plan-key') === key) {
          o.setAttribute('data-selling-plan', String(id));
        }
      });
    }
    */
    function setVariant(id) {
      if (!id || variantId) return;
      variantId = String(id);
      block.setAttribute('data-variant-id', variantId);
    }
    /* Fills a tile whose data-variant-id came out empty. Liquid resolves all
       three by id and falls back positionally, so this only fires when the
       section's product setting itself did not resolve - then the tiles are
       matched to /products/<handle>.js in DOM order, which is the same order the
       ladder is written in. It never overwrites: a Liquid-rendered id always
       wins over a positional guess. */
    function setOfferVariant(o, id) {
      if (!o || !id || o.getAttribute('data-variant-id')) return;
      o.setAttribute('data-variant-id', String(id));
    }
    function offerVariant(o) {
      return (o && o.getAttribute('data-variant-id')) || variantId;
    }
    function enableBuy() {
      if (addBtn && offerVariant(currentOffer())) addBtn.removeAttribute('disabled');
    }
    enableBuy();

    /* Recovers the variant id when Liquid could not render one, then enables the
       button. LOAD-BEARING even with subscriptions parked: without it a section
       whose product setting resolves late leaves add-to-cart permanently
       disabled.

       This used to fetch https://regaliabeauty.com/.js and regex-scrape the
       rendered home page for its data-offer labels. That worked (Shopify serves
       the page body at /.js), but it was pulling ~330 KB on every single page
       view to recover ids that /products/<handle>.js returns in about 5 KB - and
       it hard-coded the production domain, so it was cross-origin and
       CORS-blocked on every preview and development theme. */
    function reconcileFromProductJson() {
      fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) return;
          var vars = p.variants || [];
          if (vars[0]) setVariant(vars[0].id);
          offers.forEach(function (o, i) {
            if (vars[i]) setOfferVariant(o, vars[i].id);
          });

          /* SUBSCRIPTION (PARKED) - self-heal for the selling-plan ids.

             Liquid wrote data-selling-plan from sp_plans[0]/[1]/[2], which
             assumes Shopify returns the plans in 30 / 90 / 180-day order.
             Shopify does not guarantee that ordering, so this re-derived each id
             from the plan NAME and overwrote the markup.

             ORDER OF THESE TESTS IS LOAD-BEARING. The live plan names are
             "Deliver every month, 40% off", "Deliver every 3 months, 55% off"
             and "Deliver every 6 months, 65% off" - so a bare /month/ test
             matches all three. Checking 6 months, then 3 months, then the
             remaining monthly one is what keeps them apart.

             An earlier version tested /week/ first and fell through to /month/,
             which sent BOTH the 1-month and the 3-month plan to the 30-day slot
             (the 3-month one overwriting the 1-month one) and never filled the
             90-day slot at all. Anyone landing on that path would have
             subscribed to the wrong cadence.

          var plans = (((p.selling_plan_groups || [])[0]) || {}).selling_plans || [];
          plans.forEach(function (pl) {
            var n = (pl.name || '').toLowerCase();
            if (/6\s*month/.test(n)) applyPlan('180', pl.id, true);
            else if (/(3\s*month|12\s*week|90\s*day)/.test(n)) applyPlan('90', pl.id, true);
            else if (/(month|30\s*day)/.test(n)) applyPlan('30', pl.id, true);
          });

             Positional fallback for names none of the patterns recognize.
             force=false, so it can never overwrite a name-matched id above.
          if (plans[0]) applyPlan('30', plans[0].id);
          if (plans[1]) applyPlan('90', plans[1].id);
          if (plans[2]) applyPlan('180', plans[2].id);
          */
        })
        .catch(function () {})
        .then(enableBuy);
    }
    reconcileFromProductJson();

    /* ── Add to cart ────────────────────────────────────────────────────── */
    /* Exposed as window.rgpdpBuyNow because the buy box is no longer the only
       button that sells: 1-custom-pdp-sticky-atc's bar CTA is a second one and
       has to add exactly what this one adds - the tile the reader selected here,
       its unit count, this block's reconciled variant id and the parked
       selling-plan rule. All of that stays inside this closure; a caller only
       says which button it is.

       btn is the SPINNER TARGET and nothing else. Each caller passes itself, so
       the control the reader actually pressed is the one that animates - the
       sticky bar's button is on screen at exactly the moments the buy box's is
       not, which is the whole reason that bar exists.

       The variant guard is what stands in for the buy box button's disabled
       attribute. A caller outside this block cannot be disabled by enableBuy(),
       so the check has to live here, where it covers every button at once.

       QUANTITY 1, ALWAYS. Each tier is its own variant now, so the offer is
       carried by WHICH variant goes in the cart, never by how many. Sending the
       tile's data-units as a quantity - which is what this did while the ladder
       was one variant added 1x / 2x / 3x - would put three "Buy 2 Get 1 Free"
       packs in the cart and charge for nine bottles. The backend expands the
       variant into its real unit count after the order lands. */
    function buyNow(btn) {
      var o = currentOffer();
      var vid = offerVariant(o);
      if (!o || !vid || !window.rgpdpAddToCart) return;
      /* SUBSCRIPTION (PARKED) - null here is what makes every line a plain
         one-time purchase. Restore these two lines with the rest.
      var planKey = o.getAttribute('data-plan-key');
      var sellingPlan = (state.mode === 'sub' && planKey) ? planByKey[planKey] : null;
      */
      var sellingPlan = null;
      /* The free gifts this tier earns - see GIFTS GO IN THE CART in
         1-custom-pdp-hero.liquid. Empty on templates without show_gifts. */
      var giftIds = (o.getAttribute('data-gift-variants') || '').split(',').filter(Boolean);
      window.rgpdpAddToCart(vid, 1, sellingPlan, btn || addBtn, giftIds);
    }
    window.rgpdpBuyNow = buyNow;

    on(addBtn, 'click', function () { buyNow(addBtn); });
  })();

  /* ── Custom AJAX cart + slide-out drawer ────────────────────────────────── */
  (function initCart() {
    var drawer = $('[data-cart]');
    if (!drawer) return;
    var overlay = $('[data-cart-overlay]', drawer);
    var panel = $('[data-cart-panel]', drawer);
    var body = $('[data-cart-body]', drawer);
    /* The drawer below the header is a scroller and a pinned pay block. `scroller`
       holds the ship bar, the cart lines and the order summary (`foot`); `pay`
       holds the total, the checkout button and the trust row and never scrolls,
       so checkout stays on screen in a short window. `foot` and `pay` were one
       element until that split and are still shown and hidden together - render()
       writes the same `hidden` to both, and missing one of the two writes leaves
       either a floating Secure Checkout under "Your cart is empty" or a summary
       with no way to pay. Both are optional: a drawer section that has not been
       split yet still renders and still works. */
    var scroller = $('[data-cart-scroll]', drawer);
    var foot = $('[data-cart-foot]', drawer);
    var pay = $('[data-cart-pay]', drawer);
    var subtotalEl = $('[data-cart-subtotal]', drawer);
    var wasEl = $('[data-cart-was]', drawer);
    var pillEl = $('[data-cart-pill]', drawer);
    var sumSubEl = $('[data-cart-sum-sub]', drawer);
    var discRowEl = $('[data-cart-disc-row]', drawer);
    var discEl = $('[data-cart-disc]', drawer);
    var shipEl = $('[data-cart-ship]', drawer);
    var shipBar = $('[data-ship-bar]', drawer);
    var shipBarFill = $('[data-ship-bar-fill]', drawer);
    var shipBarAmount = $('[data-ship-bar-amount]', drawer);
    var tagsEl = $('[data-cart-tags]', drawer);
    var tagTpl = $('[data-cart-tag-tpl]', drawer);
    var closeEls = $$('[data-cart-close]', drawer);
    var checkoutBtn = $('[data-cart-checkout]', drawer);

    var ROUTES = { add: '/cart/add.js', clear: '/cart/clear.js', get: '/cart.js' };
    var POST = { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    var count = 0;
    var lastFocus = null;

    var buyblock = document.querySelector('[data-buyblock]');
    function attr(name, fallback) {
      var v = buyblock && buyblock.getAttribute(name);
      return (v === null || v === '') ? fallback : v;
    }
    var MONEY_SYM = attr('data-money-symbol', '$');
    var CURRENCY = attr('data-currency', '');
    /* The free-shipping threshold, in cents of the VISITOR'S currency, already
       FX-converted by 1-custom-pdp-offers and rendered onto the buy box by
       1-custom-pdp-hero. It was a quantity (FREE_SHIP_UNITS) while every tier of
       the ladder included shipping; it is a subtotal now, because the
       FREESHIPPING automatic discount in admin is one - USD 59.99 after the
       ladder's discounts, which Buy 1 does not reach.

       0 means "the buy box did not render one", and every free-shipping display
       is then driven by the tiles' own data-free-ship flags alone. */
    var FREE_SHIP_MIN = parseInt(attr('data-free-ship-min', '0'), 10) || 0;
    /* SUBSCRIPTION (PARKED) - the cosmetic "SUB&SAVE" pill. No cart line can be
       a subscription while add-to-cart sends no selling plan, so nothing pushes
       this today; kept because the attribute is still rendered. */
    var SUB_CODE = attr('data-sub-code', 'SUB&SAVE');
    function money(cents) {
      cents = Math.round(cents || 0);
      var neg = cents < 0; cents = Math.abs(cents);
      var whole = Math.floor(cents / 100), rem = cents % 100;
      var grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return (neg ? '-' : '') + MONEY_SYM + grouped + '.' + (rem < 10 ? '0' + rem : rem);
    }
    function parseMoney(txt) {
      if (!txt) return 0;
      var n = parseFloat(String(txt).replace(/[^0-9.]/g, ''));
      return isNaN(n) ? 0 : Math.round(n * 100);
    }
    /* Keyed by VARIANT ID. It was keyed by mode|quantity, which only worked while
       the ladder was one variant added 1x / 2x / 3x - a cart line's quantity was
       then enough to say which offer it was. Every tier is its own variant now
       and every line is quantity 1, so quantity says nothing and the id says
       everything. It is also the more robust key of the two: a line added from
       anywhere else on the site still matches if it happens to be one of ours.

       nowC / wasC are per ONE of that variant, so render() multiplies by the
       line's quantity. That is always 1 today; doing the multiply anyway is what
       keeps the drawer honest if a customer ever lands here with two of the same
       pack in their cart. */
    function priceTable() {
      var t = {};
      $$('[data-offer]').forEach(function (o) {
        var id = o.getAttribute('data-variant-id');
        if (!id) return;
        var nc = o.getAttribute('data-price-cents');
        var wc = o.getAttribute('data-was-cents');
        t[String(id)] = {
          name: o.getAttribute('data-name'),
          nowC: (nc !== null && nc !== '') ? parseInt(nc, 10) : parseMoney(o.getAttribute('data-price')),
          wasC: (wc !== null && wc !== '') ? parseInt(wc, 10) : parseMoney(o.getAttribute('data-was')),
          units: parseInt(o.getAttribute('data-units'), 10) || 1,
          freq: o.getAttribute('data-freq') || '',
          code: o.getAttribute('data-code') || '',
          disc: parseInt(o.getAttribute('data-discount'), 10) || 0,
          free: o.getAttribute('data-free-ship') === '1'
        };
      });
      return t;
    }

    /* The free gifts, off the DOM - the same read-the-DOM rule as priceTable().
       Keyed by variant id; `code` is the drawer tag that gift's line shows,
       from the gifts panel's data-gift-code. A cart line in this table is a
       gift buyNow() added beside the offer: render() lists it as FREE and
       keeps it out of the free-shipping and percentage logic, which belong to
       the offer line alone. Empty on templates without show_gifts, which
       leaves render() and cartBroken() exactly as they were there. */
    function giftTable() {
      var t = {};
      $$('[data-gift-variant]').forEach(function (g) {
        var id = g.getAttribute('data-gift-variant');
        if (id) t[id] = { code: g.getAttribute('data-gift-code') || '', was: g.getAttribute('data-gift-was') || '' };
      });
      return t;
    }

    /* Which gifts each OFFER variant must travel with, off the tiles'
       data-gift-variants. { offerVariantId: [giftVariantId, ...] }. */
    function giftRules() {
      var r = {};
      $$('[data-offer]').forEach(function (o) {
        var id = o.getAttribute('data-variant-id');
        if (id) r[id] = (o.getAttribute('data-gift-variants') || '').split(',').filter(Boolean);
      });
      return r;
    }

    /* ── The offer and its gifts are one unit ────────────────────────────────
       True when the cart holds an offer without the gifts its tier earns, a
       gift with no offer, or a gift that no offer in the cart earns. refresh()
       empties the cart when this is true, so a checkout can never carry a pack
       without its gifts or a gift on its own - whatever removed the line or
       added the stray one. Presence only, not quantity: every line this page
       builds is quantity 1.

       ONLY AS GOOD AS THE PAGES THIS FILE RUNS ON. A cart edited somewhere
       this script does not load is not checked until the visitor comes back
       here. The automatic discounts in admin are what keep a lone gift from
       being free at checkout. */
    function cartBroken(cart) {
      var gifts = giftTable();
      var hasGifts = false;
      for (var k in gifts) { if (gifts.hasOwnProperty(k)) { hasGifts = true; break; } }
      if (!hasGifts) return false;

      var rules = giftRules();
      var inCart = {}, need = {}, giftLines = [], offerLines = 0;
      ((cart && cart.items) || []).forEach(function (li) {
        var id = String(li.variant_id || li.id);
        inCart[id] = true;
        if (gifts[id]) giftLines.push(id);
        if (rules[id]) {
          offerLines++;
          rules[id].forEach(function (g) { need[g] = true; });
        }
      });
      if (giftLines.length && !offerLines) return true;
      for (var g in need) { if (need.hasOwnProperty(g) && !inCart[g]) return true; }
      for (var i = 0; i < giftLines.length; i++) { if (!need[giftLines[i]]) return true; }
      return false;
    }

    function notify() {
      var ev;
      try { ev = new CustomEvent('regalia:cart-updated'); }
      catch (e) { ev = document.createEvent('Event'); ev.initEvent('regalia:cart-updated', true, true); }
      document.dispatchEvent(ev);
    }

    function renderTags(codes) {
      if (!tagsEl) return;
      $$('.rgpdp-cart__tag', tagsEl).forEach(function (el) {
        if (!el.hasAttribute('data-cart-tag-tpl')) el.parentNode.removeChild(el);
      });
      if (tagTpl) {
        codes.forEach(function (c) {
          var li = tagTpl.cloneNode(true);
          li.removeAttribute('data-cart-tag-tpl');
          li.hidden = false;
          var t = li.querySelector('.rgpdp-cart__tag-text');
          if (t) t.textContent = c;
          tagsEl.appendChild(li);
        });
      }
      tagsEl.hidden = !codes.length;
    }

    /* ── The unlock-free-shipping bar ────────────────────────────────────────
       Markup and every word of copy come from 1-custom-pdp-shipbar; this writes
       three things and nothing else - the hidden attribute, the is-unlocked
       class, and the fill width plus the shortfall figure. The two sentences are
       swapped by CSS off that class, so neither is assembled here.

       The amount goes in as textContent on an element Liquid put there. Building
       the sentence as a string of HTML would mean concatenating money(), whose
       currency symbol is read off the page, into markup - and the whole point of
       leaving the wording in Liquid is that nothing here has to.

       No bar without a threshold: FREE_SHIP_MIN is 0 when the buy box did not
       render one, and a progress bar with no target to progress towards is worse
       than no bar. The unlocked half still shows, because tierFree does not need
       the threshold to be known. */
    function renderShipBar(freeShip, nowTotal) {
      if (!shipBar) return;
      if (!freeShip && FREE_SHIP_MIN <= 0) { shipBar.hidden = true; return; }

      shipBar.hidden = false;
      shipBar.classList.toggle('is-unlocked', freeShip);

      if (shipBarAmount && !freeShip) {
        shipBarAmount.textContent = money(Math.max(FREE_SHIP_MIN - nowTotal, 0));
      }
      if (shipBarFill) {
        /* Floored at 6% so an almost-empty cart still reads as a bar rather than
           a dot, and capped below 100% until it is actually earned - a full bar
           beside "you're $20 away" is the one thing this element must never
           show. */
        var pct = 100;
        if (!freeShip) {
          pct = Math.round((nowTotal / FREE_SHIP_MIN) * 100);
          pct = Math.max(6, Math.min(94, isFinite(pct) ? pct : 0));
        }
        shipBarFill.style.width = pct + '%';
      }
    }

    function render(cart) {
      count = cart ? (cart.item_count || 0) : 0;
      lastCart = cart || null;
      if (!body) return;
      body.innerHTML = '';
      var items = (cart && cart.items) || [];
      if (!items.length) {
        body.innerHTML = '<p class="rgpdp-cart__empty">Your cart is empty.</p>';
        if (foot) foot.hidden = true;
        if (pay) pay.hidden = true;
        if (shipBar) shipBar.hidden = true;
        return;
      }
      var table = priceTable();
      var gifts = giftTable();
      /* Offer line first, gifts under it, whatever order /cart.js returns the
         lines of a multi-item add in. */
      items = items.slice().sort(function (a, b) {
        return (gifts[String(a.variant_id || a.id)] ? 1 : 0) - (gifts[String(b.variant_id || b.id)] ? 1 : 0);
      });
      var nowTotal = 0, wasTotal = 0;
      /* Starts true and is ANDed down: free shipping is a property of the whole
         order, so one line that does not earn it takes it away from all of them.
         A cart of exactly one OFFER line - which is what rgpdpAddToCart always
         builds, since it clears the cart first - just takes that line's answer.
         Free-gift lines beside it return early below and never touch this. */
      var tierFree = true;
      var codes = [];
      function push(list, v) { if (v && list.indexOf(v) === -1) list.push(v); }

      items.forEach(function (li) {
        /* A gift line is priced by what Shopify says - final_line_price is 0
           once the automatic discount has applied, and the full price if it
           has not, so the drawer never claims a gift is free when checkout
           would charge for it. Its value still counts toward the Discount
           row, as it will at checkout. */
        var gift = gifts[String(li.variant_id || li.id)];
        if (gift) {
          var gNow = li.final_line_price || 0;
          var gWas = Math.max(li.original_line_price || 0, gNow);
          nowTotal += gNow;
          wasTotal += gWas;
          push(codes, gift.code);
          var gEl = document.createElement('div');
          gEl.className = 'rgpdp-cartline rgpdp-cartline--gift';
          gEl.innerHTML =
            (li.image ? '<img class="rgpdp-cartline__img" src="' + li.image + '" alt="" width="64" height="64">'
                      : '<span class="rgpdp-cartline__img"></span>') +
            '<div class="rgpdp-cartline__main">' +
              '<div class="rgpdp-cartline__name"></div>' +
              '<span class="rgpdp-cartline__meta">FREE Gift</span>' +
            '</div>' +
            '<div class="rgpdp-cartline__prices">' +
              '<span class="rgpdp-cartline__price">' + (gNow === 0 ? 'FREE' : money(gNow)) + '</span>' +
              /* Discounted gift: strike what Shopify says it cost. Gift priced
                 at 0 in admin: there is no discount to strike, so show the
                 panel's compare-at value instead - display only, never added
                 to wasTotal, because checkout will not show that saving. */
              (gWas > gNow ? '<s class="rgpdp-cartline__was">' + money(gWas) + '</s>'
                : (gNow === 0 && gift.was ? '<s class="rgpdp-cartline__was"></s>' : '')) +
            '</div>' +
            /* Same data-cart-remove as the offer line, so the same clearCart():
               removing a gift empties the cart, offer and all. */
            '<button type="button" class="rgpdp-cartline__remove" data-cart-remove ' +
              'aria-label="Remove from cart" title="Remove from cart">&times;</button>';
          gEl.querySelector('.rgpdp-cartline__name').textContent = li.product_title || li.title;
          var gWasEl = gEl.querySelector('.rgpdp-cartline__was');
          if (gWasEl && !gWasEl.textContent) gWasEl.textContent = gift.was;
          body.appendChild(gEl);
          return;
        }

        var isSub = !!li.selling_plan_allocation;
        var info = table[String(li.variant_id || li.id)];
        var qty = li.quantity || 1;
        var nowC = info ? info.nowC * qty : li.final_line_price;
        var wasC = info ? info.wasC * qty : (li.original_line_price || li.final_line_price);
        if (wasC < nowC) wasC = nowC;
        nowTotal += nowC;
        wasTotal += wasC;
        if (!info || !info.free) tierFree = false;

        if (isSub) push(codes, SUB_CODE);
        if (wasC > nowC) {
          push(codes, (info && info.code) || '');
        }

        var freq = info && info.freq ? info.freq : (isSub ? 'Subscription' : 'One-time purchase');
        var img = li.image || '';
        var el = document.createElement('div');
        el.className = 'rgpdp-cartline';
        el.innerHTML =
          (img ? '<img class="rgpdp-cartline__img" src="' + img + '" alt="" width="64" height="64">'
               : '<span class="rgpdp-cartline__img"></span>') +
          '<div class="rgpdp-cartline__main">' +
            '<div class="rgpdp-cartline__name"></div>' +
            '<span class="rgpdp-cartline__meta"></span>' +
            (isSub ? '<span class="rgpdp-cartline__badge">Subscription</span>' : '') +
          '</div>' +
          '<div class="rgpdp-cartline__prices">' +
            '<span class="rgpdp-cartline__price">' + money(nowC) + '</span>' +
            (wasC > nowC ? '<s class="rgpdp-cartline__was">' + money(wasC) + '</s>' : '') +
          '</div>' +
          '<button type="button" class="rgpdp-cartline__remove" data-cart-remove ' +
            'aria-label="Remove from cart" title="Remove from cart">&times;</button>';
        /* The PRODUCT title from Shopify admin, the same name checkout leads
           the line with. The pack is already said by the meta line under it
           ("3 bottles · One-time purchase"), so the variant's own name (the
           tile's data-name) would only repeat it. data-name falls in only if
           Shopify sends no product title. */
        el.querySelector('.rgpdp-cartline__name').textContent = li.product_title || (info && info.name) || li.title;
        /* Bottles SHIPPED, not the line quantity. The quantity is 1 on every
           offer line now and would read "1 bottle" under a five-bottle pack. */
        var bottles = (info ? info.units : 1) * qty;
        el.querySelector('.rgpdp-cartline__meta').textContent =
          bottles + (bottles === 1 ? ' bottle' : ' bottles') + ' · ' + freq;
        body.appendChild(el);
      });

      var saved = wasTotal - nowTotal;
      renderTags(codes);

      if (sumSubEl) sumSubEl.textContent = money(wasTotal);
      if (discEl) discEl.textContent = '-' + money(saved);
      if (discRowEl) discRowEl.hidden = saved <= 0;

      /* ── Free shipping ─────────────────────────────────────────────────────
         Buy 1 does not earn it; Buy 2 Get 1 Free and Buy 3 Get 2 Free do. Which
         is which is decided ONCE, in Liquid, against the converted threshold -
         see the free-shipping block in 1-custom-pdp-offers - and arrives here as
         each tile's data-free-ship. Recomputing it from the money in here would
         be a second opinion that could disagree with the tiles.

         The money test is the fallback for a line that is not one of ours (no
         table entry, so tierFree is already false) and for a page where the buy
         box did not render a threshold. Never a quantity: the offer with the
         MOST bottles in it is not necessarily the one over the threshold, and
         with the packs at quantity 1 a quantity test cannot separate the tiers
         at all. */
      var freeShip = tierFree || (FREE_SHIP_MIN > 0 && nowTotal >= FREE_SHIP_MIN);
      if (shipEl) {
        shipEl.textContent = freeShip ? 'FREE SHIPPING' : 'Calculated at checkout';
        shipEl.classList.toggle('rgpdp-cart__sum-val--good', freeShip);
      }
      renderShipBar(freeShip, nowTotal);

      /* The ONE saving on this page shown as a percentage rather than a money
         amount. Every other savings figure - the offer tiles' Save pills and the
         drawer's Discount row - is a dollar figure; this pill sits on the final
         total and reads "60% Off". Deliberate contrast, do not "make consistent".

         Always worked out from the two figures it sits beside - the struck
         total and the total - so the pill and the prices next to it can never
         disagree. It used to read the tile's marketed percentage (data-discount)
         for a single line, but those under-promise on purpose (berberine AUD
         reads 50 / 50 / 55 against real 53 / 53 / 58), and a pill that does not
         match the arithmetic beside it reads as a mistake. */
      if (pillEl) {
        var pct = wasTotal > 0 ? Math.round((saved / wasTotal) * 100) : 0;
        if (pct > 0) { pillEl.textContent = pct + '% Off'; pillEl.hidden = false; }
        else pillEl.hidden = true;
      }

      if (subtotalEl) {
        subtotalEl.textContent = money(nowTotal);
        if (CURRENCY) {
          var cur = document.createElement('span');
          cur.className = 'rgpdp-cart__cur';
          cur.textContent = CURRENCY;
          subtotalEl.appendChild(document.createTextNode(' '));
          subtotalEl.appendChild(cur);
        }
      }
      if (wasEl) {
        if (saved > 0) { wasEl.textContent = money(wasTotal); wasEl.hidden = false; }
        else wasEl.hidden = true;
      }
      if (foot) foot.hidden = false;
      if (pay) pay.hidden = false;
    }

    /* Every read of the cart comes through here - page load, add to cart, open,
       remove - so this is where cartBroken() is enforced: a cart holding a pack
       without its gifts or a gift without its pack is emptied before it is ever
       drawn, and the drawer then renders the empty cart. */
    function refresh() {
      function get() {
        return fetch(ROUTES.get, { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); });
      }
      return get()
        .then(function (c) {
          if (!cartBroken(c)) return c;
          return fetch(ROUTES.clear, POST).then(get);
        })
        .then(function (c) { render(c); notify(); return c; })
        .catch(function () {});
    }

    var isOpen = false;

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocus = document.activeElement;
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      /* Open at the top, every time. The drawer is re-opened rather than
         re-built, so without this a customer who scrolled down to the summary,
         closed, and added again would come back to a panel already scrolled past
         the ship bar and the line they just added. Safe here because both call
         sites await refresh() first, so what is being measured is the new cart. */
      if (scroller) scroller.scrollTop = 0;
      lockScroll(true);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { drawer.classList.add('is-open'); });
      });
      var closeBtn = $('[data-cart-close]', drawer);
      if (closeBtn && closeBtn.focus) closeBtn.focus();
    }
    function close() {
      if (!isOpen) return;
      isOpen = false;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      var done = function () { drawer.hidden = true; if (panel) panel.removeEventListener('transitionend', done); };
      if (panel && !reduceMotion.matches) {
        panel.addEventListener('transitionend', done);
        setTimeout(function () { if (!drawer.classList.contains('is-open')) drawer.hidden = true; }, 400);
      } else {
        drawer.hidden = true;
      }
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    window.regaliaOpenCart = function () { refresh().then(open); };
    window.regaliaCartCount = function () { return count; };

    /* giftIds: the free-gift variants the selected tier earns, one of each.
       They go in the SAME request as the offer, and that is deliberate: a
       /cart/add.js with several items is all-or-nothing, so the pack and its
       gifts land together or not at all - the cart can never be left holding
       one without the other (cartBroken() would only empty it again). The
       price of that: a gift that is unavailable in a market, or out of stock,
       refuses the whole add. Keep the gift products published, in stock and
       in every market. */
    window.rgpdpAddToCart = function (variantId, qty, sellingPlan, btn, giftIds) {
      if (btn) btn.classList.add('is-busy');
      fetch(ROUTES.clear, POST)
        .then(function () {
          var item = { id: variantId, quantity: qty };
          if (sellingPlan) item.selling_plan = sellingPlan;
          var items = [item];
          (giftIds || []).forEach(function (id) { items.push({ id: id, quantity: 1 }); });
          return fetch(ROUTES.add, {
            method: 'POST',
            headers: POST.headers,
            body: JSON.stringify({ items: items })
          });
        })
        .then(function (r) {
          if (!r.ok) return r.text().then(function (t) { throw new Error(t || r.status); });
          return r.json();
        })
        .then(function () { return refresh(); })
        .then(function () {
          if (btn) btn.classList.remove('is-busy');
          trackOffer(null, 'regalia_add_to_cart');
          open();
        })
        .catch(function (err) {
          if (btn) btn.classList.remove('is-busy');
          if (window.console) console.error('[rgpdp cart]', err);
          window.alert('Sorry — we could not add that to your cart. Please try again.');
        });
    };

    function clearCart(btn) {
      if (btn) btn.disabled = true;
      fetch(ROUTES.clear, POST)
        .then(refresh)
        .catch(function () {})
        .then(function () { if (btn) btn.disabled = false; });
    }
    on(body, 'click', function (e) {
      var rm = e.target && e.target.closest ? e.target.closest('[data-cart-remove]') : null;
      if (rm) { e.preventDefault(); clearCart(rm); }
    });

    /* ── Checkout ─────────────────────────────────────────────────────────── */
    /* NO preventDefault. The button is a real <a href="/checkout"> and this
       handler only measures the click - the browser does the navigating. That
       is deliberate: checkout keeps working if this file fails to load, and
       middle-click / "open in new tab" behave like any other link.

       This used to intercept the click and open a waitlist popup instead, which
       is why the drawer prices are our own and the real reduction only lands at
       checkout - the ladder is a set of AUTOMATIC discounts in admin
       (BUY1GET-xx / BUY2GET1-xx / BUY3GET2-xx + FREESHIPPING, see
       1-custom-pdp-offers), so nothing has to be applied here for the customer
       to be charged the advertised figure.

       metaEvent is null ON PURPOSE, same reason as AddToCart. Now that checkout
       is real, Shopify's own pixel fires checkout_started - which the Facebook &
       Instagram app maps to InitiateCheckout, over both pixel and CAPI. Our own
       fb('InitiateCheckout') would carry an eventID Shopify never saw, so Meta
       would COUNT IT TWICE rather than deduplicate it. See the header of
       1-custom-track.js.

       sendBeacon because navigation starts immediately: PostHog's normal
       transport would be cancelled in flight and the event lost. */
    on(checkoutBtn, 'click', function () {
      trackOffer(null, 'regalia_initiate_checkout', null, { transport: 'sendBeacon' });
    });

    closeEls.forEach(function (el) { on(el, 'click', close); });
    on(overlay, 'click', close);
    on(document, 'keydown', function (e) {
      if (e.key !== 'Escape' || drawer.hidden) return;
      close();
    });

    refresh();
  })();

  /* ── Sticky CTA + permanent recall button ──────────────────────────────── */
  (function initSticky() {
    var bar = $('[data-sticky]');
    var toTop = $('[data-to-top]');
    var trigger = $('[data-sticky-trigger]');

    /* barHeight is cached because reading offsetHeight inside a scroll handler
       forces a synchronous layout on every frame. It only changes on resize. */
    var barHeight = 0;
    function measureBar() { if (bar) barHeight = bar.offsetHeight; }

    /* Two visibility modes, selected by data-sticky-mode on the bar itself.

       (absent)        Show once the buy box has scrolled completely past. The
                       buy box is the FIRST thing on index.json and
                       page.pdp-adv1.json, so "past it" means "still reading",
                       which is exactly when a recall bar earns its place.

       until-trigger   The inverse: show from the top of the page and hide the
                       moment the buy box comes into view. This is for templates
                       that put a long story ABOVE the buy box
                       (page.pdp-adv6.json). There the bar's job is to carry a
                       reader DOWN to an offer they have not reached yet, and it
                       would be pure noise sitting over the offer itself.

       between-triggers
                       For templates that open with a listicle whose own CTA
                       jumps down to the buy box (1-custom-pdp-listicle). Hidden
                       until that CTA - [data-sticky-guard] - has scrolled off the
                       top of the screen, hidden again while ANY part of the buy
                       box is on screen, shown everywhere else: through the
                       listicle once its button is gone, and after the buy box.
                       No guard on the page falls back to "a screen's travel".

       Absent is the default on purpose: every existing template omits the
       attribute and must keep behaving exactly as it does today. */
    var UNTIL = bar && bar.getAttribute('data-sticky-mode') === 'until-trigger';
    var BETWEEN = bar && bar.getAttribute('data-sticky-mode') === 'between-triggers';
    var guard = $('[data-sticky-guard]');

    function apply() {
      var past;
      if (!trigger) {
        past = window.pageYOffset > window.innerHeight;
      } else if (UNTIL) {
        /* Two conditions, not one. The buy box must still be below the fold -
           that is the "hide when they reach it" half - AND the reader must have
           travelled about a screen first. Without the second test the bar and
           the back-to-top button would both be showing at scroll position 0,
           where there is nothing yet to recall anyone to and "back to top" is a
           dead control pointing at where they already are. */
        past = trigger.getBoundingClientRect().top > window.innerHeight &&
               getTop(activeScroller()) > window.innerHeight * 0.6;
      } else if (BETWEEN) {
        var box = trigger.getBoundingClientRect();
        var boxInView = box.bottom > 0 && box.top < window.innerHeight;
        var guardGone = guard
          ? guard.getBoundingClientRect().bottom < 0
          : getTop(activeScroller()) > window.innerHeight;
        past = guardGone && !boxInView;
      } else {
        past = trigger.getBoundingClientRect().bottom < 0;
      }

      if (bar) {
        bar.classList.toggle('is-visible', past);
        bar.setAttribute('aria-hidden', past ? 'false' : 'true');
        /* The footer reserves space for the sticky bar off this variable, so it
           has to be cleared when the bar hides or the footer keeps a gap. */
        document.documentElement.style.setProperty(
          '--rgpdp-sticky-space', past ? (barHeight + 14) + 'px' : '0px'
        );
      }
      if (toTop) toTop.classList.toggle('is-visible', past);
    }

    /* Coalesced to one run per animation frame. This is listened for on BOTH
       document (capture) and window because at >=990px the theme scrolls
       .page-wrapper rather than the document, and a scroll on that element does
       not bubble to window - so neither listener alone covers both breakpoints.
       Without the rAF gate that meant two forced layouts per scroll event. */
    var ticking = false;
    function update() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; apply(); });
    }
    function updateNow() { measureBar(); apply(); }

    document.addEventListener('scroll', update, { passive: true, capture: true });
    on(window, 'scroll', update, { passive: true });
    on(window, 'resize', updateNow, { passive: true });
    on(window, 'load', updateNow);
    document.addEventListener('shopify:section:load', updateNow);

    if ('IntersectionObserver' in window && trigger) {
      var stickyIo = new IntersectionObserver(update, { threshold: [0, 1] });
      stickyIo.observe(trigger);
      if (BETWEEN && guard) stickyIo.observe(guard);
    }
    updateNow();

    /* Moving focus back to the <h1> is what stops keyboard users being stranded
       mid-page after a scroll-to-top. The catch: whether that programmatic focus
       draws a :focus-visible ring is an ENGINE HEURISTIC. Blink (desktop Chrome,
       and DevTools device mode, which is still Blink) suppresses it after a
       pointer activation, so this never reproduces there; WebKit (Safari iOS,
       and Chrome on iOS, which is a WKWebView) matches :focus-visible on a
       programmatic focus regardless, so the title gets outlined on tap.

       So: keep the ring when the button was activated by keyboard, silence it
       when it was a tap or click. A click synthesized from Enter/Space reports
       detail === 0; real pointer clicks report >= 1. */
    var titleFocus = $('[data-offer-focus]');
    on(titleFocus, 'blur', function () {
      titleFocus.removeAttribute('data-focus-quiet');
    });

    on(toTop, 'click', function (e) {
      /* data-to-top="page" scrolls to the true top of the document rather than
         to the buy box. Needed on any template where the buy box is NOT the
         first thing on the page (page.pdp-adv6.json): there, scrollIntoView on
         [data-top-anchor] would send an up-arrow button DOWNWARDS. The
         attribute is absent everywhere else, so the buy-box behaviour below
         stays the default.

         The focus move is deliberately skipped in page mode - the buy box is
         not where the reader just went, so focusing its <h1> would announce the
         wrong place. Focus simply stays on the button, which is still on screen. */
      if (toTop.getAttribute('data-to-top') === 'page') {
        var sc = activeScroller();
        var behavior = reduceMotion.matches ? 'auto' : 'smooth';
        try {
          if (isWindowScroller(sc)) window.scrollTo({ top: 0, left: 0, behavior: behavior });
          else sc.scrollTo({ top: 0, left: 0, behavior: behavior });
        } catch (err) { setTop(sc, 0); }
        return;
      }

      var top = $('[data-top-anchor]') || root;
      scrollToEl(top);
      if (titleFocus) {
        var byKeyboard = !!e && e.detail === 0;
        if (byKeyboard) titleFocus.removeAttribute('data-focus-quiet');
        else titleFocus.setAttribute('data-focus-quiet', '');
        titleFocus.setAttribute('tabindex', '-1');
        try { titleFocus.focus({ preventScroll: true }); } catch (err) { titleFocus.focus(); }
      }
    });
  })();

  /* ── Cross-breakpoint scroll transfer (the blank-screen fix) ────────────── */
  (function initScrollFix() {
    if (!window.matchMedia) return;

    var lastActive = activeScroller();
    var lastTop = getTop(lastActive);
    var lastMax = maxTop(lastActive);

    function record(e) {
      if (!isFromActiveScroller(e)) return;
      var active = activeScroller();
      lastActive = active;
      lastTop = getTop(active);
      lastMax = maxTop(active);
    }
    document.addEventListener('scroll', record, { passive: true, capture: true });
    on(window, 'scroll', record, { passive: true });

    function zeroInactive(to) {
      var pw = pageWrapper();
      if (!isWindowScroller(to)) {
        try { window.scrollTo(0, 0); } catch (e) {}
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      }
      if (pw && pw !== to) pw.scrollTop = 0;
    }

    function transfer() {
      var to = activeScroller();
      var frac = lastMax > 0 ? Math.min(1, Math.max(0, lastTop / lastMax)) : 0;

      function apply() {
        zeroInactive(to);
        setTop(to, frac * maxTop(to));
      }
      apply();
      requestAnimationFrame(apply);
      setTimeout(apply, 60);
      setTimeout(apply, 160);
      setTimeout(function () {
        apply();
        lastActive = to;
        lastTop = getTop(to);
        lastMax = maxTop(to);
      }, 320);
    }

    if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', transfer);
    else if (DESKTOP.addListener) DESKTOP.addListener(transfer);
  })();

  /* ── Scroll position restore ───────────────────────────────────────────── */
  (function initScrollRestore() {
    var key = 'rgpdp:scroll:' + location.pathname;

    var pending = false;
    document.addEventListener('scroll', function (e) {
      if (!isFromActiveScroller(e)) return;
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        try { sessionStorage.setItem(key, String(getTop(activeScroller()))); }
        catch (err) {  }
      });
    }, { passive: true, capture: true });

    if (location.hash && document.getElementById(location.hash.slice(1))) return;

    on(window, 'load', function () {
      var saved = null;
      try { saved = sessionStorage.getItem(key); } catch (e) { return; }
      if (saved === null) return;
      var y = parseInt(saved, 10);
      if (!y || y < 0) return;
      setTop(activeScroller(), y);
    });
  })();

  /* ── In-page CTA links ─────────────────────────────────────────────────── */
  (function initJumpLinks() {
    $$('[data-jump]').forEach(function (link) {
      on(link, 'click', function (e) {
        var id = link.getAttribute('data-jump');
        var target = id ? document.getElementById(id) : null;
        if (!target) return;
        e.preventDefault();
        scrollToEl(target);
      });
    });
  })();

  /* ── CTAs that add to cart where they stand ────────────────────────────── */
  /* data-atc turns any button on the page into the buy box's add-to-cart. The
     bar in 1-custom-pdp-sticky-atc is what uses it: its CTA sells from wherever
     the reader happens to be, with no jump first.

     NO SCROLL, DELIBERATELY. An earlier version scrolled to the buy box and
     sequenced the add behind it, and it read badly for a reason that is obvious
     once seen: this bar hides itself the moment the buy box comes into view, so
     the reader watched the button they had just pressed travel up the page and
     disappear before anything happened. A control that says ADD TO CART should
     add to cart and nothing else. The arrow beside it is what exists for going
     back to the offer, and it still does.

     window.rgpdpBuyNow is initBuyBox's own add path - same selected tile, same
     unit count, same variant id, same tracking, same drawer - and handing it
     `btn` makes THIS button the one that spins. Nothing here behaves differently
     from the buy box's button except where it sits on the screen.

     No double-tap guard is needed. rgpdpAddToCart puts is-busy on the button it
     was handed, and .rgpdp-addcart.is-busy is pointer-events:none - the same
     thing that already protects the buy box's own button. */
  (function initAtcLinks() {
    $$('[data-atc]').forEach(function (btn) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        if (window.rgpdpBuyNow) window.rgpdpBuyNow(btn);
      });
    });
  })();

  /* ── Named PostHog click events ────────────────────────────────────────── */
  /* Any element carrying data-track-event="name" sends a PostHog event called
     `name` when it is clicked, and nothing else changes: the click still
     navigates, jumps or adds to cart exactly as before. The name comes from a
     section setting (track_event on 1-custom-lp-intro, 1-custom-lp-offer,
     1-custom-lp-summary and 1-custom-pdp-sticky-atc), so which buttons report,
     and under what name, is decided per template, not here. Elements without
     the attribute are ignored, so every page that sets nothing is untouched.

     POSTHOG ONLY, like the advertorials' CTA block: Meta's funnel is counted
     on arrival at the PDP, and a browser-only departure event would read low
     beside it. One delegated listener in the capture phase, so it runs before
     the navigation and before any stopPropagation lower down. */
  (function initTrackedClicks() {
    function scrollDepthPct() {
      var s = activeScroller();
      var top = getTop(s);
      var max = isWindowScroller(s)
        ? ((document.documentElement.scrollHeight || 0) - window.innerHeight)
        : (s.scrollHeight - s.clientHeight);
      if (max <= 0) return 0;
      return Math.min(100, Math.round((top / max) * 100));
    }

    on(document, 'click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('[data-track-event]') : null;
      if (!el) return;
      var name = el.getAttribute('data-track-event');
      var t = RT();
      if (!name || !t) return;
      t.capture(name, join(t.clickContext(el), {
        scroll_depth_pct: scrollDepthPct(),
        page_path: location.pathname,
        template: (window.Theme && window.Theme.template && window.Theme.template.name) || ''
      }), {
        /* The link navigates as soon as this handler returns; without
           send_instantly the event can sit in PostHog's batch queue and be
           lost when the page unloads. */
        send_instantly: true
      });
    }, true);
  })();

  /* ── Video diagnostics plumbing (?rgdebug=1) ───────────────────────────── */
  var VDEBUG = /[?&]rgdebug=1/.test(location.search);
  var VLOG_KEY = 'rgpdp:vlog';
  var vlogLines = [];

  function vlog(msg) {
    if (!VDEBUG) return;
    var t = (performance.now() / 1000).toFixed(1);
    vlogLines.push(t + 's  ' + msg);
    if (vlogLines.length > 120) vlogLines.shift();
    try { sessionStorage.setItem(VLOG_KEY, vlogLines.join('\n')); } catch (e) {}
  }

  /* ── Video: play only while in view ────────────────────────────────────── */
  (function initVideos() {
    var videos = $$('video[data-inview]');
    if (!videos.length) return;

    function revive(v, force) {
      var purged = force ||
                   v.readyState === 0 ||
                   v.networkState === 3  ||
                   (v.videoWidth === 0 && v.networkState !== 2 );
      vlog((force ? 'force-reload #' : 'revive #') + v.rgpdpIndex +
           ' ready=' + v.readyState + ' net=' + v.networkState +
           ' vw=' + v.videoWidth + (purged ? ' -> load()' : ''));
      if (purged) { try { v.load(); } catch (e) {} }
      var p = v.play();
      if (p && p.catch) p.catch(function (err) {
        vlog('play REJECTED #' + v.rgpdpIndex + ' ' + (err && err.name));
      });
    }

    videos.forEach(function (v, i) { v.rgpdpIndex = i + 1; });

    if (!('IntersectionObserver' in window)) {
      videos.forEach(function (v) { try { revive(v); } catch (e) {} });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        v.rgpdpInView = entry.isIntersecting;
        if (entry.isIntersecting) {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
    }, { rootMargin: '100px 0px' });

    videos.forEach(function (v) { observer.observe(v); });

    function restore() {
      if (document.visibilityState === 'hidden') return;
      vlog('--- restore (' + document.visibilityState + ') ---');
      var live = videos.filter(function (v) { return v.rgpdpInView; });
      live.forEach(function (v) {
        var was = v.currentTime;
        revive(v);
        setTimeout(function () {
          if (!v.rgpdpInView) return;
          if (v.paused || v.currentTime === was || v.videoWidth === 0) {
            revive(v, true);
          }
        }, 500);
      });
    }

    on(document, 'visibilitychange', restore);
    on(window, 'pageshow', function (e) {
      vlog('pageshow persisted=' + !!(e && e.persisted));
      restore();
    });
    on(window, 'pagehide', function (e) {
      vlog('pagehide persisted=' + !!(e && e.persisted));
    });

    var wdTime = {}, wdTry = {}, wdStalls = {};
    setInterval(function () {
      if (document.visibilityState === 'hidden') return;
      var now = Date.now();
      videos.forEach(function (v) {
        var i = v.rgpdpIndex;
        if (!v.rgpdpInView) { wdTime[i] = -1; wdStalls[i] = 0; return; }

        if (v.paused) {
          if (now - (wdTry[i] || 0) < 2000) return;
          wdTry[i] = now;
          vlog('watchdog #' + i + ' paused while in view');
          revive(v);
          return;
        }

        if (v.readyState < 3) return;

        if (v.currentTime === wdTime[i]) {
          wdStalls[i] = (wdStalls[i] || 0) + 1;
          if (wdStalls[i] >= 2 && now - (wdTry[i] || 0) > 5000) {
            wdTry[i] = now;
            wdStalls[i] = 0;
            vlog('watchdog #' + i + ' frozen at ' + v.currentTime.toFixed(1) + ', forcing reload');
            revive(v, true);
          }
        } else {
          wdStalls[i] = 0;
        }
        wdTime[i] = v.currentTime;
      });
    }, 1000);
  })();

  /* ── UGC videos: tap to play, tap to pause ─────────────────────────────────
     1-custom-pdp-hero's ugc_video blocks. Deliberately NOT data-inview, so
     initVideos() above never touches them: these must never autoplay.

     LOADED LAST. The mp4 waits in data-ugc-src until the window's load event
     has fired AND the carousel is within 600px of the viewport. Only then does
     it go into src, with #t=0.001 and preload="metadata" - just enough for the
     browser (iOS Safari included) to paint the first frame. The rest of the
     file downloads only when someone presses play.

     Play unmutes, because the reader asked for it by tapping. Starting one
     pauses every other, and a clip pauses itself when it scrolls off screen or
     the tab is hidden, so the page is never talking from somewhere the reader
     cannot see. There is no controls attribute, so no scrubbing: play and
     pause are the only two things a tap can do.

     The track is plain scroll-snap, so swiping is the platform's. All this
     does for the carousel is drive the bar underneath, both ways - the bar
     follows the swipe, and dragging the bar scrolls the track (see DRAGGING
     THE BAR below). --ugc-progress is how
     far along the scroll range the track is, 0 to 1, read on every scroll
     frame rather than debounced so the thumb moves WITH the finger. A row
     that fits without scrolling hides the bar. */
  (function initUgcVideos() {
    var all = $$('[data-ugc] video');
    if (!all.length) return;

    function pauseOthers(keep) {
      all.forEach(function (v) { if (v !== keep && !v.paused) v.pause(); });
    }

    on(document, 'visibilitychange', function () {
      if (document.visibilityState === 'hidden') pauseOthers(null);
    });

    $$('[data-ugc]').forEach(function (ugc) {
      var track = $('[data-ugc-track]', ugc);
      if (!track) return;

      var items = $$('.rgpdp-ugc__item', track);
      var thumb = $('[data-ugc-thumb]', ugc);
      var rail = thumb ? thumb.parentNode : null;
      var bar = $('[data-ugc-bar]', ugc);

      function prime(v, frag) {
        if (v.getAttribute('src')) return;
        v.preload = 'metadata';
        v.src = v.getAttribute('data-ugc-src') + (frag ? '#t=0.001' : '');
      }

      function primeAll() {
        items.forEach(function (item) {
          var v = $('video', item);
          if (v) prime(v, true);
        });
      }

      function watch() {
        if (!('IntersectionObserver' in window)) { primeAll(); return; }
        var near = new IntersectionObserver(function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) {
            near.disconnect();
            primeAll();
          }
        }, { rootMargin: '600px 0px' });
        near.observe(ugc);
      }

      if (document.readyState === 'complete') watch();
      else on(window, 'load', watch);

      /* Pause on leaving the screen. Separate from the observer above, which
         disconnects after it has primed. */
      var gone = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting && !e.target.paused) e.target.pause();
        });
      }) : null;

      items.forEach(function (item) {
        var v = $('video', item);
        var btn = $('[data-ugc-toggle]', item);
        if (!v || !btn) return;

        function setState(playing) {
          item.classList.toggle('is-playing', playing);
          btn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        }

        on(v, 'play', function () { setState(true); });
        on(v, 'pause', function () { setState(false); });
        on(v, 'ended', function () { setState(false); });

        on(btn, 'click', function () {
          if (!v.paused && !v.ended) { v.pause(); return; }
          /* A tap that beats the load event or the near observer primes this
             one itself - no #t, it is about to play from the start anyway. */
          prime(v, false);
          pauseOthers(v);
          v.muted = false;
          var p = v.play();
          if (p && p.catch) p.catch(function () { setState(false); });
        });

        if (gone) gone.observe(v);
      });

      if (!bar || !rail) return;

      var ticking = false;

      function paint() {
        ticking = false;
        var max = track.scrollWidth - track.clientWidth;
        bar.hidden = max <= 1;
        var progress = max > 0 ? Math.max(0, Math.min(track.scrollLeft / max, 1)) : 0;
        ugc.style.setProperty('--ugc-progress', progress.toFixed(4));
      }

      function schedule() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(paint);
      }

      on(track, 'scroll', schedule, { passive: true });
      on(window, 'resize', debounce(paint, 200), { passive: true });
      paint();

      /* DRAGGING THE BAR. Pressing on the thumb grabs it where the finger
         landed; pressing anywhere else on the bar centres the thumb under the
         finger and carries on as a drag from there, so a tap is a jump. The
         thumb's position maps straight onto the track's scroll range - its
         travel is the rail minus its own width, the same 60% the CSS moves it
         across - and the scroll event repaints it, so the bar and the track
         cannot disagree. Snap is off for the drag (.is-dragging) and the track
         is eased onto the nearest clip edge when the finger lifts. */
      var drag = null;

      function dragTo(clientX) {
        var travel = rail.clientWidth - thumb.offsetWidth;
        var max = track.scrollWidth - track.clientWidth;
        if (travel <= 0 || max <= 0) return;
        var x = clientX - rail.getBoundingClientRect().left - drag.grab;
        track.scrollLeft = Math.max(0, Math.min(x / travel, 1)) * max;
      }

      /* Clips past the end of the range all clamp to it, which is exactly where
         mandatory snap would leave them too. */
      function snapTarget(at) {
        var max = Math.max(0, track.scrollWidth - track.clientWidth);
        var base = items[0] ? items[0].offsetLeft : 0;
        var best = 0, bestDist = Infinity;
        items.forEach(function (item) {
          var left = Math.min(item.offsetLeft - base, max);
          var dist = Math.abs(left - at);
          if (dist < bestDist) { bestDist = dist; best = left; }
        });
        return best;
      }

      on(bar, 'pointerdown', function (e) {
        if (e.button !== 0) return;
        e.preventDefault();
        var t = thumb.getBoundingClientRect();
        var onThumb = e.clientX >= t.left && e.clientX <= t.right;
        drag = { id: e.pointerId, grab: onThumb ? e.clientX - t.left : t.width / 2 };
        ugc.classList.add('is-dragging');
        try { bar.setPointerCapture(e.pointerId); } catch (err) {}
        dragTo(e.clientX);
      });

      on(bar, 'pointermove', function (e) {
        if (drag && e.pointerId === drag.id) dragTo(e.clientX);
      });

      function endDrag(e) {
        if (!drag || e.pointerId !== drag.id) return;
        drag = null;
        ugc.classList.remove('is-dragging');
        var left = snapTarget(track.scrollLeft);
        try {
          track.scrollTo({ left: left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        } catch (err) {
          track.scrollLeft = left;
        }
      }

      on(bar, 'pointerup', endDrag);
      on(bar, 'pointercancel', endDrag);
    });
  })();

  /* ── On-device video diagnostics (?rgdebug=1) ──────────────────────────── */
  if (VDEBUG) (function initVideoDebug() {
    var prev = '';
    try { prev = sessionStorage.getItem(VLOG_KEY) || ''; } catch (e) {}
    if (prev) vlogLines = prev.split('\n');
    vlog('=== page load (nav type: ' +
         ((performance.getEntriesByType && (performance.getEntriesByType('navigation')[0] || {}).type) || '?') +
         ') ===');

    var panel = document.createElement('div');
    panel.setAttribute('style', [
      'position:fixed', 'left:0', 'right:0', 'top:0', 'z-index:2000',
      'max-height:52vh', 'overflow:auto', '-webkit-overflow-scrolling:touch',
      'background:rgba(0,0,0,.92)', 'color:#0f0', 'font:11px/1.35 ui-monospace,Menlo,monospace',
      'padding:8px 10px 10px', 'white-space:pre', 'text-align:left'
    ].join(';'));
    document.body.appendChild(panel);

    var collapsed = false;
    on(panel, 'click', function () {
      collapsed = !collapsed;
      panel.style.maxHeight = collapsed ? '2.6em' : '52vh';
    });

    ['emptied', 'error', 'stalled', 'suspend', 'abort', 'waiting', 'loadedmetadata']
      .forEach(function (evt) {
        $$('video[data-inview]').forEach(function (v) {
          on(v, evt, function () { vlog(evt + ' #' + v.rgpdpIndex); });
        });
      });

    on(document, 'freeze', function () { vlog('LIFECYCLE freeze'); });
    on(document, 'resume', function () { vlog('LIFECYCLE resume'); });
    on(document, 'visibilitychange', function () {
      vlog('visibility -> ' + document.visibilityState);
    });

    function render() {
      var out = 'RGPDP video debug   dpr=' + (window.devicePixelRatio || 1) +
                '  vw=' + window.innerWidth + '\n' +
                '# box(css)   video    rdy net pl  t     src\n';
      $$('video[data-inview]').forEach(function (v) {
        var r = v.getBoundingClientRect();
        var src = (v.currentSrc || '').split('.').pop().split('?')[0] || '-';
        out += v.rgpdpIndex + ' ' +
               pad(Math.round(r.width) + 'x' + Math.round(r.height), 10) +
               pad(v.videoWidth + 'x' + v.videoHeight, 9) +
               pad(v.readyState, 4) + pad(v.networkState, 4) +
               pad(v.paused ? 'Y' : 'n', 4) +
               pad(v.currentTime.toFixed(1), 6) + src +
               (v.error ? ' ERR' + v.error.code : '') +
               (v.rgpdpInView ? ' [view]' : '') + '\n';
      });
      panel.textContent = out + '\n' + vlogLines.slice(-40).join('\n');
    }

    function pad(s, n) {
      s = String(s);
      while (s.length < n) s += ' ';
      return s;
    }

    setInterval(render, 500);
    render();
  })();

  /* ── Dispatch date (today + 2 business days) ───────────────────────────── */
  (function initShipDate() {
    var d = new Date();
    var added = 0;
    while (added < 2) {
      d.setDate(d.getDate() + 1);
      var wd = d.getDay();
      if (wd !== 0 && wd !== 6) added++;
    }

    var days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var label = days[d.getDay()] + ', ' + months[d.getMonth()] + ' ' + d.getDate();

    /* One writer, one hook. This used to also rebuild the buy box's whole stock
       line through [data-ship-text], which meant the sentence existed twice -
       once in the section and once in here - and the two drifted. The section
       now owns the wording and marks the date with [data-ship-date], the same
       hook the cart drawer already used, so this only ever writes the date. */
    $$('[data-ship-date]').forEach(function (el) { el.textContent = label; });
  })();

  /* ── Sold bar: count 0 -> target the first time it is seen ─────────────── */
  /* Runs once per card, on first sight - not on load. The markup ships at 0
     (see the sold bar comment in 1-custom-pdp-hero.liquid), so a card below
     the fold just sits at 0% until it scrolls in, and one above it counts the
     moment it is half on screen.

     The curve is an exponential saturation, normalised so it lands exactly on
     the target at the end of DURATION instead of creeping towards it forever:
        f(t) = (1 - e^(-K t)) / (1 - e^(-K)),  t in 0..1
     K sets how hard it eases out - at 5 the number covers about 80% of the
     distance in the first third and spends the rest settling. The fill's
     width rides the same value so the bar and the figure never disagree. */
  (function initSoldBar() {
    var DURATION = 4200;
    var K = 10;
    var NORM = 1 - Math.exp(-K);

    $$('[data-sold]').forEach(function (card) {
      var target = parseInt(card.getAttribute('data-sold-target'), 10) || 0;
      var fill = $('[data-sold-fill]', card);
      var num = $('[data-sold-num]', card);
      var done = false;

      function paint(p) {
        if (fill) fill.style.width = p + '%';
        if (num) num.textContent = Math.round(p);
      }

      function run() {
        if (done) return;
        done = true;
        var start = null;
        function frame(ts) {
          if (start === null) start = ts;
          var t = Math.min(1, (ts - start) / DURATION);
          paint(t < 1 ? target * (1 - Math.exp(-K * t)) / NORM : target);
          if (t < 1) requestAnimationFrame(frame);
        }
        requestAnimationFrame(frame);
      }

      if (!('IntersectionObserver' in window)) {
        run();
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        if (entries[entries.length - 1].isIntersecting) {
          io.disconnect();
          run();
        }
      }, { threshold: 0.5 });
      io.observe(card);
    });
  })();

  /* ── Figure band: count 0 -> target, staggered, on first sight ─────────── */
  /* 1-custom-pdp-numbers. The same normalised saturation curve as the sold
     bar above, with the same K, so the two counts on the page move alike.

     The stagger is in the DURATION, not in a delay: every figure that comes
     into view together starts counting together, and each one after the
     first is given STEP ms longer, so the first lands first, then the second,
     and so on. On a desktop row all four arrive in one observer callback and
     stagger across the row; on a phone, where they stack, they mostly arrive
     one at a time as the page scrolls, and any that do share a callback still
     stagger top to bottom - entries come back in observe() order, which is
     DOM order.

     The span is observed, not the band: on a phone the band is taller than
     the screen, and a figure counting while it is still below the fold is a
     count nobody sees.

     THRESHOLD IS 0.5, NOT 1. At 1 the count never started on a screen with
     display scaling: the span's box lands on fractional pixels, its ratio
     tops out a hair under 1.0, the threshold is never crossed and the figure
     sits at 0 for good. Half on screen is also what the sold bar waits for.

     No reduced-motion shortcut, to match the sold bar: Windows reports
     reduce whenever its animation effects are off, which on a lot of desktops
     would have meant these never counted at all. */
  (function initCountUp() {
    var DURATION = 3000;
    var STEP = 800;
    var K = 10;
    var NORM = 1 - Math.exp(-K);

    var nums = $$('[data-count-num]');
    if (!nums.length) return;

    function paint(el, v) {
      el.textContent = Math.round(v).toLocaleString('en-US');
    }

    function run(el, duration) {
      var target = parseInt(el.getAttribute('data-count-target'), 10) || 0;
      var start = null;
      function frame(ts) {
        if (start === null) start = ts;
        var t = Math.min(1, (ts - start) / duration);
        paint(el, t < 1 ? target * (1 - Math.exp(-K * t)) / NORM : target);
        if (t < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }

    if (!('IntersectionObserver' in window)) {
      nums.forEach(function (el, i) { run(el, DURATION + i * STEP); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      var n = 0;
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        run(entry.target, DURATION + n * STEP);
        n++;
      });
    }, { threshold: 0.5 });
    nums.forEach(function (el) { io.observe(el); });
  })();

  /* ── Accordions: one open at a time, both ends animated ─────────────────── */
  /* NOTHING TOUCHES THE SCROLL POSITION ANY MORE. The version before this one
     closed the group's other row in a single frame and then subtracted the
     height that row had given back out of the scroll offset, so the tapped
     summary never moved a pixel. On paper that is stability. In the hand it
     read as the page seizing up under the finger, because everything around
     the finger jumped while the finger's own row sat still.

     So the correction is gone, and the jump it was correcting is gone with it.
     A row now changes height over a fifth to a half of a second, the page
     reflows around it for every one of those frames, and the reader watches
     the copy below slide down to make room instead of finding it already
     somewhere else. Where the page ends up is wherever the content puts it -
     which is the point.

     <details> cannot do this by itself: the browser swaps the body in and out
     in one frame, and the CSS that would interpolate that - ::details-content
     with interpolate-size: allow-keywords - is not in Safari, which is most of
     the traffic here. So the summary's click is taken over. Default prevented,
     `open` written by hand, and the ROW's height animated between its two
     measured states through the Web Animations API. box-sizing is border-box
     inside .rgpdp, so a height set here is the height the row occupies,
     borders included, and .rgpdp-acc already carries overflow: hidden, which
     is what clips the body while it is part of the way out.

     Both heights are measured at the moment of the tap and never cached. The
     copy re-wraps at 768, again in the two-column .rgpdp-acc-grid, and again
     whenever an image inside a body finishes loading.

     Duration follows the distance, between DUR_MIN and DUR_MAX. One flat
     number cannot serve both ends of this - a one-line FAQ answer at 420ms
     feels broken, and a six-bullet hero accordion at 200ms throws the page.

     Interruption is the normal case and not an edge case: tapping a second row
     while the first is still moving. The replacement animation starts from the
     height the row had actually reached, and seq retires the callbacks of the
     one it replaced - cancel() fires oncancel a task later, and a stale
     handler would clear the height its replacement had just set.

     .rgpdp-acc-moving sits on <html> for exactly as long as something is
     moving. It turns off the browser's own scroll anchoring, which otherwise
     watches the height change, decides the reader is losing their place and
     pulls the offset back the other way - the same lock this rewrite exists to
     remove, only written by Chrome instead of by us. It comes straight off
     again, so lazily-loaded images further down the page keep their anchoring.

     No JS at all still leaves five sections of working native <details>. */
  (function initAccordions() {
    /* DUR_MIN is the floor every row pays to get moving at all, and the height
       it has to travel buys time on top of it up to DUR_MAX. Roughly: a
       one-line FAQ answer opens in about 210ms, a six-bullet hero accordion in
       about 330ms, and anything past ~480px of travel is capped. */
    var DUR_MIN = 180;       /* ms */
    var DUR_MAX = 420;       /* ms - past this it drags */
    var MS_PER_PX = 0.5;     /* ms of animation per px of height change */
    var EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
    var GROUP = '.rgpdp-acc-grid, .rgpdp-faq__list';

    var canAnimate = !!(window.Element && Element.prototype && Element.prototype.animate);

    /* EVERY accordion on the page, not just the ones standing in a group of
       two or more the way the old version collected them - the animation is
       wanted on all of them, and a lone row simply has nothing to close. */
    var rows = [];
    $$('details.rgpdp-acc').forEach(function (el) {
      var summary = $('summary', el);
      if (!summary) return;
      rows.push({
        el: el,
        summary: summary,
        group: el.closest ? el.closest(GROUP) : null,
        /* The state it is heading FOR, which is not el.open while it closes. */
        want: el.open,
        anim: null,
        seq: 0
      });
    });
    if (!rows.length) return;

    function mates(row) {
      return rows.filter(function (o) {
        return o !== row && o.group && o.group === row.group;
      });
    }

    function find(el) {
      for (var i = 0; i < rows.length; i++) if (rows[i].el === el) return rows[i];
      return null;
    }

    /* The state a row is HEADING for, which is the only question worth asking
       of it. While it moves that is `want`, because a row half way shut is
       still open as far as the DOM is concerned and tapping it again has to
       re-open it rather than shut it twice. While it is at rest the DOM is the
       truth instead - Chrome's find-in-page opens a <details> to show a match
       inside it and never goes near the click handler, so `want` would be
       stale and the next tap would appear to do nothing. */
    function isOpen(row) {
      return row.anim ? row.want : row.el.open;
    }

    function markMoving() {
      var live = rows.some(function (o) { return !!o.anim; });
      document.documentElement.classList.toggle('rgpdp-acc-moving', live);
    }

    /* The height of the row with the body hidden: the summary's own box plus
       whatever the <details> puts around it. Read live rather than cached
       because the forced-colors rule doubles the border. */
    function shutHeight(row) {
      var cs = window.getComputedStyle(row.el);
      return row.summary.offsetHeight +
        (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0) +
        (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    }

    /* The height it wants with the body showing. The inline height is let go
       for the length of one read - getBoundingClientRect forces layout, so the
       number is the real one, and nothing paints between the two assignments
       so the swap is never on screen. Only valid while open is true. */
    function openHeight(row) {
      var held = row.el.style.height;
      row.el.style.height = 'auto';
      var h = row.el.getBoundingClientRect().height;
      row.el.style.height = held;
      return h;
    }

    /* Straight to the end state, no animation at all. Used for the hash, where
       the browser is already jumping to the row and a height still growing
       underneath it would land that jump short, and as the fallback for a
       browser with no Element.animate. */
    function set(row, to) {
      if (row.anim) { row.anim.cancel(); row.anim = null; }
      row.seq++;
      row.want = to;
      row.el.open = to;
      row.el.style.height = '';
      markMoving();
    }

    function run(row, to) {
      if (!canAnimate) { set(row, to); return; }

      /* Read where the row IS before anything is cancelled, so one that gets
         interrupted carries on from the height it had reached. */
      var from = row.el.getBoundingClientRect().height;

      if (row.anim) { row.anim.cancel(); row.anim = null; }
      var seq = ++row.seq;
      row.want = to;

      /* Open either way. Closing needs the body in the DOM for as long as it
         is still being taken away; open flips back to false at the end. */
      row.el.open = true;
      row.el.style.height = from + 'px';

      var target = to ? openHeight(row) : shutHeight(row);
      var travel = Math.abs(target - from);

      function settle() {
        if (seq !== row.seq) return;
        row.anim = null;
        row.el.open = to;
        row.el.style.height = '';
        markMoving();
      }

      if (travel < 1) { settle(); return; }

      /* Written as the inline height as well as animated to, so the frame in
         which the finished animation is removed already has the end state
         underneath it. A running animation outranks the inline style, so this
         is invisible right up until it is the truth. */
      row.el.style.height = target + 'px';

      row.anim = row.el.animate(
        [{ height: from + 'px' }, { height: target + 'px' }],
        {
          duration: Math.min(DUR_MAX, DUR_MIN + travel * MS_PER_PX),
          easing: EASE
        }
      );
      row.anim.onfinish = settle;
      row.anim.oncancel = function () {
        if (seq !== row.seq) return;
        row.anim = null;
        markMoving();
      };
      markMoving();
    }

    rows.forEach(function (row) {
      on(row.summary, 'click', function (e) {
        /* A link or a button inside a heading keeps its own job - the
           preventDefault below would otherwise take its navigation with it. */
        if (e.target && e.target.closest && e.target.closest('a, button')) return;

        e.preventDefault();

        /* The mates are closed in the same task as this one opens, so their
           animations run alongside it and the page sees the two height changes
           as one net movement rather than a collapse followed by a growth. */
        var opening = !isOpen(row);
        if (opening) {
          mates(row).forEach(function (o) { if (isOpen(o)) run(o, false); });
        }
        run(row, opening);
      });
    });

    function openFromHash() {
      var hash = window.location.hash;
      if (!hash || hash.length < 2) return;
      var target;
      try { target = document.querySelector(hash); } catch (e) { return; }
      if (!target) return;
      var det = target.closest ? target.closest('details.rgpdp-acc') : null;
      if (!det && target.tagName === 'DETAILS') det = target;
      if (!det) return;
      var row = find(det);
      if (!row || isOpen(row)) return;
      mates(row).forEach(function (o) { if (isOpen(o)) set(o, false); });
      set(row, true);
    }

    on(window, 'hashchange', openFromHash);
    openFromHash();
  })();

  /* ── Comparison table: the two scroll rails ────────────────────────────────
     The comparison is a four column table and only about three of them fit on
     a phone, so it scrolls sideways inside .rgpdp-compare-scroll. This draws
     the handles for that scroll: one rail above the table and one below, both
     driving the same element.

     Two of them, because the table is roughly a screen tall. Read the column
     headers at the top of it and the only native scrollbar is off the bottom
     of the screen; read the last rows and the reverse. Either rail is in reach
     from wherever you happen to be looking.

     Drawn rather than native for the one reason that matters - size. Overlay
     scrollbars on macOS and iOS are a few pixels of grey that fade out the
     moment you stop scrolling, nothing in CSS makes them any bigger, and this
     is the only way to reach a whole column of the comparison. These are 14px
     of maroon that never fade, and the thumb never goes under 44px.

     Progressive enhancement, in this order. The CSS ships the container's own
     scrollbar styled thick and the rails start [hidden] in the markup, so with
     no JS the table still scrolls and still says so. measure() reveals the
     rails and puts .is-railed on the wrapper - which is what hides the native
     bar - and only once it has actually found an overflow to scroll. On a
     desktop, where all four columns fit, nothing appears at all. */
  (function initCompareRails() {
    var MIN_THUMB = 44;   /* px - a thumb you can still hit with a thumb */

    $$('[data-compare]').forEach(function (wrap) {
      var scroller = $('[data-compare-scroll]', wrap);
      var rails = $$('[data-compare-rail]', wrap);
      if (!scroller || !rails.length) return;

      var thumbs = [];
      var missing = false;
      rails.forEach(function (rail) {
        var t = $('[data-compare-thumb]', rail);
        if (!t) missing = true;
        thumbs.push(t);
      });
      if (missing) return;

      var max = 0;        /* px of scroll available, 0 when it all fits */
      var queued = false;

      function clamp(v) { return v < 0 ? 0 : (v > max ? max : v); }

      /* smooth only for the trough press, which is a jump the eye has to
         follow. A drag writes this every pointermove and has to land where the
         finger is, not somewhere behind it. */
      function slideTo(left, smooth) {
        left = clamp(left);
        try {
          scroller.scrollTo({ left: left, behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto' });
        } catch (e) {
          scroller.scrollLeft = left;
        }
      }

      function paint() {
        if (max <= 0) return;
        var frac = scroller.scrollLeft / max;
        if (frac < 0) frac = 0;
        if (frac > 1) frac = 1;
        rails.forEach(function (rail, i) {
          var travel = rail.clientWidth - thumbs[i].offsetWidth;
          if (travel < 0) travel = 0;
          thumbs[i].style.transform = 'translateX(' + (frac * travel).toFixed(1) + 'px)';
        });
      }

      function measure() {
        max = scroller.scrollWidth - scroller.clientWidth;
        /* 1 and not 0: a fixed-layout table lands on a fractional pixel often
           enough that an exact test would show both rails on a desktop where
           there is nothing to scroll. */
        var scrollable = max > 1;

        if (scrollable) wrap.classList.add('is-railed');
        else wrap.classList.remove('is-railed');

        rails.forEach(function (rail) {
          if (scrollable) rail.removeAttribute('hidden');
          else rail.setAttribute('hidden', '');
        });

        if (!scrollable) { max = 0; return; }

        /* Width has to be written after the [hidden] is off, or every rail
           measures 0 wide and every thumb comes out at the 44px floor. */
        rails.forEach(function (rail, i) {
          var track = rail.clientWidth;
          var w = Math.round(track * scroller.clientWidth / scroller.scrollWidth);
          if (w < MIN_THUMB) w = MIN_THUMB;
          if (w > track) w = track;
          thumbs[i].style.width = w + 'px';
        });

        paint();
      }

      rails.forEach(function (rail, i) {
        var thumb = thumbs[i];
        var down = false, fromX = 0, fromLeft = 0;

        function start(e) {
          if (max <= 0) return;
          down = true;
          fromX = e.clientX;
          fromLeft = scroller.scrollLeft;
          rail.classList.add('is-dragging');
          if (e.pointerId != null && thumb.setPointerCapture) {
            try { thumb.setPointerCapture(e.pointerId); } catch (err) {}
          }
          /* preventDefault stops the press selecting the table behind it;
             stopPropagation keeps it out of the trough handler below. */
          e.preventDefault();
          e.stopPropagation();
        }

        function move(e) {
          if (!down) return;
          var travel = rail.clientWidth - thumb.offsetWidth;
          if (travel <= 0) return;
          /* max / travel is the gearing: the thumb crosses its own track in
             the same gesture that takes the table from end to end. */
          slideTo(fromLeft + (e.clientX - fromX) * (max / travel), false);
        }

        function stop() {
          if (!down) return;
          down = false;
          rail.classList.remove('is-dragging');
        }

        on(thumb, 'pointerdown', start);
        on(thumb, 'pointermove', move);
        on(thumb, 'pointerup', stop);
        on(thumb, 'pointercancel', stop);
        /* The capture is meant to send pointerup back to the thumb wherever
           the finger lifts. This is the release that does not arrive. */
        on(window, 'pointerup', stop);

        /* A press on the bare track pages one screen toward the press, the way
           a native trough does. */
        on(rail, 'pointerdown', function (e) {
          if (max <= 0 || e.target === thumb) return;
          var box = thumb.getBoundingClientRect();
          var step = scroller.clientWidth * 0.85;
          slideTo(scroller.scrollLeft + (e.clientX > box.left + box.width / 2 ? step : -step), true);
        });
      });

      on(scroller, 'scroll', function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () { queued = false; paint(); });
      }, { passive: true });

      var remeasure = debounce(measure, 120);
      on(window, 'resize', remeasure);
      on(window, 'orientationchange', remeasure);

      if (window.ResizeObserver) {
        var ro = new ResizeObserver(remeasure);
        ro.observe(scroller);
        var table = $('.rgpdp-compare', scroller);
        if (table) ro.observe(table);
      }

      /* The bottle and the two column icons are lazy, and the bottle is the
         tallest thing in the table, so nothing about this table's size is
         final until they land. */
      $$('img', scroller).forEach(function (img) {
        if (!img.complete) on(img, 'load', remeasure);
      });

      measure();
    });
  })();

  /* ── Listicle gallery: autoplay, segmented bar, hold to pause ─────────────
     1-custom-pdp-listicle. The swiping is the platform's - an overflow-x track
     with scroll-snap - so this only drives the autoplay and the bar.

     TWO CLOCKS, ON PURPOSE (see the section file's header):

       barStart   the bar. Set when a slide becomes current and never touched
                  by the reader - holding the image does not stop or rewind
                  the fill, it runs to full and stays there.
       advanceAt  the timer. Zeroed while a finger or mouse button is down,
                  set to now + DURATION on release. The slide only moves when
                  this runs out, so the bar is always full before it does.

     A swipe to another slide is picked up from the scroll position once it
     settles, and that slide's segment starts from empty. Segments before the
     current one draw full, after it empty, like a stories bar.

     Touch events, not pointer events: the browser fires pointercancel the
     moment a touch turns into a native pan, which would read as "let go" while
     the finger is still on the glass. touchend fires when it actually lifts.

     Paused off screen and in a background tab. Coming back restarts the
     current slide's segment and timer from zero rather than advancing on a
     clock the reader never saw. Past the last image it slides back to the
     first; the bar does not rewind segment by segment on the way, because
     the current index is set when the slide starts, not read off the scroll
     position mid-travel. */
  (function initListicle() {
    $$('[data-listicle]').forEach(function (gallery) {
      var track = $('[data-listicle-track]', gallery);
      if (!track) return;
      var slides = $$('.rglist__slide', track);
      var fills = $$('[data-listicle-fill]', gallery);
      if (slides.length < 2) return;

      var DURATION = parseInt(gallery.getAttribute('data-listicle-ms'), 10) || 4000;
      var current = 0;
      var barStart = 0;
      var advanceAt = 0;
      var held = false;
      var inView = !('IntersectionObserver' in window);
      var running = false;
      var raf = 0;

      function now() { return window.performance ? performance.now() : Date.now(); }

      function paint() {
        var p = Math.min(1, (now() - barStart) / DURATION);
        fills.forEach(function (f, i) {
          var v = i < current ? 1 : (i === current ? p : 0);
          f.style.transform = 'scaleX(' + v + ')';
        });
      }

      function start(index) {
        current = index;
        barStart = now();
        advanceAt = held ? 0 : barStart + DURATION;
        paint();
      }

      /* The slide itself is animated HERE, a frame at a time, and not with
         scrollTo({ behavior: 'smooth' }). WebKit - every browser on an iPhone -
         jumps rather than animates a smooth scrollTo on a scroll-snap track, so
         the autoplay advance snapped across while a finger swipe (the browser's
         own scrolling) glided. Ease-in-out cubic: gentle off the mark and
         into the landing, like a flick that settles.

         Snapping is switched OFF for the length of the animation and back on
         when it lands, for the reason .rgproof__track's CSS note gives:
         `mandatory` treats every per-frame scrollLeft write as a settle and
         drags the track back to the nearest edge, so the slide would judder in
         place. The last frame writes the exact target, so turning snapping back
         on finds the track already on a snap point and nothing moves.

         A touch mid-animation stops it where it is (hold() calls stopSlide)
         and hands the track back to the finger, snapping restored. */
      var SLIDE_MS = 520;
      var slideRaf = 0;

      function stopSlide() {
        if (!slideRaf) return;
        cancelAnimationFrame(slideRaf);
        slideRaf = 0;
        track.style.scrollSnapType = '';
      }

      function ease(p) {
        return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
      }

      /* ALWAYS ANIMATES, reduced-motion or not, like everything else on the
         site - honouring it here turned every advance back into the instant
         flick this function exists to replace. */
      function slideTo(left) {
        stopSlide();
        var from = track.scrollLeft;
        if (Math.abs(left - from) < 1) {
          track.scrollLeft = left;
          return;
        }
        /* Longer the further it travels, so the wrap back to the first image
           reads as a slide rather than a blur. Capped well inside the timer. */
        var hops = Math.round(Math.abs(left - from) / track.clientWidth);
        var dur = Math.min(900, SLIDE_MS + 120 * Math.max(0, hops - 1));
        var t0 = now();
        track.style.scrollSnapType = 'none';
        function step() {
          var p = Math.min(1, (now() - t0) / dur);
          track.scrollLeft = p < 1 ? from + (left - from) * ease(p) : left;
          if (p < 1) {
            slideRaf = requestAnimationFrame(step);
          } else {
            slideRaf = 0;
            track.style.scrollSnapType = '';
          }
        }
        slideRaf = requestAnimationFrame(step);
      }

      function goTo(index) {
        index = (index + slides.length) % slides.length;
        slideTo(index * track.clientWidth);
        start(index);
      }

      function tick() {
        raf = 0;
        if (!running) return;
        if (!held && advanceAt && now() >= advanceAt) goTo(current + 1);
        else paint();
        raf = requestAnimationFrame(tick);
      }

      function setRunning() {
        var should = inView && !document.hidden;
        if (should === running) return;
        running = should;
        if (running) {
          start(current);
          if (!raf) raf = requestAnimationFrame(tick);
        }
      }

      /* Settled position -> current slide. Skipped while held so a finger
         parked mid-drag is not read as having landed anywhere; release()
         re-runs it. An autoplay scroll lands on the index goTo() already set,
         so it is a no-op there. */
      var syncFromScroll = debounce(function () {
        if (held || !track.clientWidth) return;
        var index = Math.round(track.scrollLeft / track.clientWidth);
        index = Math.max(0, Math.min(index, slides.length - 1));
        if (index !== current) start(index);
      }, 100);

      function hold() {
        stopSlide();
        held = true;
        advanceAt = 0;
      }
      function release() {
        if (!held) return;
        held = false;
        advanceAt = now() + DURATION;
        syncFromScroll();
      }

      on(track, 'scroll', syncFromScroll, { passive: true });
      on(track, 'touchstart', hold, { passive: true });
      on(track, 'touchend', function (e) { if (!e.touches.length) release(); });
      on(track, 'touchcancel', function (e) { if (!e.touches.length) release(); });
      on(track, 'mousedown', hold);
      on(window, 'mouseup', release);

      /* Arrows sit beside the track, not in it, so pressing one is not a hold.
         goTo() starts the new slide's segment from empty and its timer from
         zero, the same as a swipe. Both wrap round the ends. */
      on($('[data-listicle-prev]', gallery), 'click', function () { goTo(current - 1); });
      on($('[data-listicle-next]', gallery), 'click', function () { goTo(current + 1); });

      /* Arrow keys scroll the focused track natively. A key press restarts the
         timer the way a touch does, without holding it - focus alone must not
         pause it, because tapping the track on a phone focuses it too. */
      on(track, 'keydown', function () { if (!held) advanceAt = now() + DURATION; });

      on(document, 'visibilitychange', setRunning);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          inView = entries[entries.length - 1].isIntersecting;
          setRunning();
        }).observe(gallery);
      }

      start(0);
      setRunning();
    });
  })();


  /* ── Study drawer ─────────────────────────────────────────────────────────
     The slide-out in 1-custom-pdp-study-drawer: a list of the studies, and
     each study's title, authors and abstract behind it. Every view is already
     in the markup; all this does is open, close, and swap which one shows.

     ONE TRIGGER: any [data-rgstudy-open] button, found by delegation on the
     document so it can sit in any section. It opens the list; the reader
     picks a study from there.

     The shell - hidden until opened, is-open a frame later so the slide runs,
     the shared lockScroll() - is initCart's open()/close(), and closes the
     same way. */
  (function initStudyDrawer() {
    var drawer = $('[data-rgstudy]');
    if (!drawer) return;
    var panel = $('.rgstudy__panel', drawer);
    var scroller = $('[data-rgstudy-scroll]', drawer);
    var titleEl = $('[data-rgstudy-title]', drawer);
    var backBtn = $('[data-rgstudy-back]', drawer);
    var closeBtn = $('.rgstudy__close', drawer);
    var views = $$('[data-rgstudy-view]', drawer);

    /* Only now that there is a drawer to open do the buttons appear. */
    $$('[data-rgstudy-open]').forEach(function (btn) { btn.hidden = false; });

    var isOpen = false;
    var lastFocus = null;
    var listFocus = null;

    function show(id) {
      var target = null;
      views.forEach(function (v) {
        var match = v.getAttribute('data-rgstudy-view') === id;
        v.hidden = !match;
        if (match) target = v;
      });
      if (!target) {
        if (id !== 'list') show('list');
        return;
      }
      var isList = id === 'list';
      if (titleEl) titleEl.hidden = !isList;
      if (backBtn) backBtn.hidden = isList;
      if (scroller) scroller.scrollTop = 0;
    }

    function focusables() {
      return $$('button, a[href]', panel).filter(function (el) {
        return !el.hidden && !el.closest('[hidden]');
      });
    }

    function open(id) {
      show(id || 'list');
      if (!isOpen) {
        isOpen = true;
        lastFocus = document.activeElement;
        drawer.hidden = false;
        drawer.setAttribute('aria-hidden', 'false');
        lockScroll(true);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { drawer.classList.add('is-open'); });
        });
      }
      var first = id && id !== 'list' ? backBtn : closeBtn;
      if (first && first.focus) first.focus({ preventScroll: true });
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      /* Both hide paths check isOpen, so a close followed quickly by a re-open
         cannot have the first close's late transitionend hide the new one. */
      var done = function () {
        if (panel) panel.removeEventListener('transitionend', done);
        if (!isOpen) drawer.hidden = true;
      };
      if (panel && !reduceMotion.matches) {
        panel.addEventListener('transitionend', done);
        setTimeout(function () { if (!isOpen) drawer.hidden = true; }, 400);
      } else {
        drawer.hidden = true;
      }
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    on(document, 'click', function (e) {
      var t = e.target && e.target.closest ? e.target : null;
      if (!t) return;

      if (drawer.contains(t)) {
        if (t.closest('[data-rgstudy-close]')) { close(); return; }
        if (t.closest('[data-rgstudy-back]')) {
          show('list');
          if (listFocus && listFocus.focus) listFocus.focus({ preventScroll: true });
          return;
        }
        var item = t.closest('[data-rgstudy-show]');
        if (item) {
          listFocus = item;
          open(item.getAttribute('data-rgstudy-show'));
        }
        return;
      }

      if (t.closest('[data-rgstudy-open]')) { listFocus = null; open('list'); }
    });

    on(document, 'keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;

      var list = focusables();
      if (!list.length) { e.preventDefault(); return; }
      var first = list[0];
      var last = list[list.length - 1];
      var here = document.activeElement;
      if (!panel.contains(here)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && here === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && here === last) { e.preventDefault(); first.focus(); }
    });
  })();

})();
